<template>
  <div class="ivr-builder-root">
    <!-- Top Bar -->
    <div class="ivr-builder-topbar">
      <button class="ivr-builder-back" @click="$router.push('/ivr/flows')" title="Back to flows">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
        IVR Flows
      </button>

      <div class="ivr-builder-flow-meta">
        <input
          v-model="flowName"
          class="ivr-builder-name-input"
          placeholder="Flow name"
          @blur="scheduleSave"
        />
        <div class="ivr-builder-did-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
          <input v-model="triggerNumber" class="ivr-builder-did-input" placeholder="Trigger DID (optional)" @blur="scheduleSave" />
        </div>
      </div>

      <div class="ivr-builder-topbar-right">
        <span class="ivr-save-indicator" :class="saveStatus">
          <svg v-if="saveStatus === 'saving'" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
          <svg v-else-if="saveStatus === 'saved'" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
          <svg v-else-if="saveStatus === 'error'" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <svg v-else-if="saveStatus === 'unsaved'" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>
          {{ saveStatusText }}
        </span>

        <!-- Manual save button (shown when auto-save is off and there are unsaved changes) -->
        <button
          v-if="!autoSaveEnabled && saveStatus === 'unsaved'"
          class="ivr-manual-save-btn"
          @click="autoSave"
          title="Save now (Ctrl+S)"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>
          Save
        </button>

        <!-- Auto-save toggle -->
        <button
          class="ivr-autosave-toggle"
          :class="{ on: autoSaveEnabled }"
          @click="autoSaveEnabled = !autoSaveEnabled"
          :title="autoSaveEnabled ? 'Auto-save is ON — click to disable' : 'Auto-save is OFF — click to enable'"
        >
          <span class="ivr-autosave-track">
            <span class="ivr-autosave-thumb"></span>
          </span>
          Auto-save
        </button>

        <button
          class="ivr-builder-status-btn"
          :class="flowStatus === 'active' ? 'active' : 'inactive'"
          @click="toggleFlowStatus"
        >
          <span class="status-dot"></span>
          {{ flowStatus === 'active' ? 'Active' : 'Activate' }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ivr-builder-loading">
      <div class="ivr-spinner"></div>
      <p>Loading flow...</p>
    </div>

    <div v-else class="ivr-builder-workspace">
      <!-- Left Palette -->
      <div class="ivr-palette" :class="{ collapsed: paletteCollapsed }">
        <div class="ivr-palette-header" @click="paletteCollapsed = !paletteCollapsed">
          <span v-if="!paletteCollapsed" class="palette-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-16v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/></svg>
            Nodes
          </span>
          <svg class="palette-chevron" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path v-if="!paletteCollapsed" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            <path v-else d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </div>
        <div v-if="!paletteCollapsed" class="ivr-palette-search">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="palette-search-icon"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          <input
            v-model="paletteSearch"
            class="palette-search-input"
            placeholder="Search nodes…"
            @click.stop
          />
          <button v-if="paletteSearch" class="palette-search-clear" @click.stop="paletteSearch = ''" title="Clear">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        <div v-if="!paletteCollapsed" class="ivr-palette-body">
          <div v-if="filteredNodeGroups.length === 0" class="palette-no-results">No nodes match "{{ paletteSearch }}"</div>
          <div v-for="group in filteredNodeGroups" :key="group.label" class="ivr-palette-group">
            <div class="ivr-palette-group-label">{{ group.label }}</div>
            <div
              v-for="nt in group.types"
              :key="nt.type"
              class="ivr-palette-node"
              :style="{ '--node-color': nt.color }"
              draggable="true"
              @dragstart="onPaletteDragStart($event, nt)"
              :title="nt.description"
            >
              <span class="ivr-palette-node-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path :d="getNodeIconPath(nt.type)" />
                </svg>
              </span>
              <div class="ivr-palette-node-info">
                <span class="ivr-palette-node-name">{{ nt.label }}</span>
                <span class="ivr-palette-node-desc">{{ nt.description }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Canvas -->
      <div
        class="ivr-canvas"
        ref="canvasRef"
        @dragover.prevent="onCanvasDragOver"
        @drop="onCanvasDrop"
        @click="onCanvasClick"
        @mousedown="onCanvasMouseDown"
        @mousemove="onCanvasMouseMove"
        @mouseup="onCanvasMouseUp"
        @wheel="onWheel"
      >
        <svg class="ivr-edge-layer" :width="canvasW" :height="canvasH">
          <defs>
            <marker id="arrowhead" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
              <polygon points="0 0, 7 2.5, 0 5" fill="#94a3b8" fill-opacity="0.9" />
            </marker>
          </defs>
          <!-- single transform group — edgePath uses local node coordinates, no pan/zoom math inside paths -->
          <g :transform="`translate(${pan.x},${pan.y}) scale(${zoom})`">
            <g v-for="edge in computedEdges" :key="edge.id" @click.stop="selectEdge(edge.id)" class="ivr-edge-group">
              <!-- invisible wide hit area -->
              <path :d="edge.path" fill="none" stroke="transparent" stroke-width="14" pointer-events="stroke" />
              <!-- visible stroke -->
              <path
                :d="edge.path"
                fill="none"
                stroke="#94a3b8"
                stroke-width="2"
                stroke-opacity="0.7"
                stroke-linecap="round"
                marker-end="url(#arrowhead)"
                class="ivr-edge"
                :class="{ selected: selectedEdge === edge.id }"
              />
            </g>
          </g>
        </svg>

        <!-- Floating disconnect button shown at selected edge midpoint -->
        <div
          v-if="selectedEdge && selectedEdgeMidpoint"
          class="ivr-edge-disconnect-btn"
          :style="{ left: selectedEdgeMidpoint.x + 'px', top: selectedEdgeMidpoint.y + 'px' }"
          title="Disconnect wire"
          @click.stop="deleteEdge(selectedEdge)"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </div>

        <div class="ivr-nodes-layer" :style="transformStyle">
          <div
            v-for="node in flowNodes"
            :key="node.id"
            class="ivr-node"
            :class="[
              'ivr-node-' + node.ivrType,
              { selected: selectedNodeId === node.id, 'is-start': flowJson.start_node === node.id }
            ]"
            :style="{ left: node.position.x + 'px', top: node.position.y + 'px', '--node-accent': getNodeColor(node.ivrType) }"
            @mousedown.stop="startNodeDrag($event, node)"
            @click.stop="selectNode(node)"
          >
            <span v-if="flowJson.start_node === node.id" class="ivr-node-start-badge">START</span>
            <div class="ivr-node-inner">
              <div class="ivr-node-icon-col">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path :d="getNodeIconPath(node.ivrType)" />
                </svg>
              </div>
              <div class="ivr-node-text-col">
                <div class="ivr-node-name">{{ node.name }}</div>
                <div class="ivr-node-sub">{{ getNodeLabel(node.ivrType) }}<template v-if="getNodeSummary(node)"> · {{ getNodeSummary(node) }}</template></div>
              </div>
              <button class="ivr-node-delete-btn" @click.stop="promptDeleteNode(node)" title="Delete node">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
              </button>
            </div>

            <!-- Input handle — drop target only, never a drag source -->
            <div class="ivr-handle ivr-handle-input"
              :data-node="node.id"
              data-handle="input"
              @mousedown.stop
            ></div>

            <!-- Output handles -->
            <template v-if="isBranchingNode(node.ivrType)">
              <div
                v-for="(routeKey, idx) in getRouteKeys(node)"
                :key="routeKey"
                class="ivr-handle ivr-handle-output ivr-handle-route"
                :style="{ bottom: -(26 + idx * 24) + 'px' }"
                :data-node="node.id"
                :data-handle="routeKey"
                @mousedown.stop="startEdgeDraw($event, node.id, routeKey)"
                :title="routeKey"
              >
                <span class="ivr-route-label">{{ routeKey }}</span>
              </div>
            </template>
            <div v-else-if="node.ivrType !== 'ivr_end'"
              class="ivr-handle ivr-handle-output"
              :data-node="node.id"
              data-handle="next"
              @mousedown.stop="startEdgeDraw($event, node.id, 'next')"
            ></div>
          </div>
        </div>

        <!-- Drawing edge preview -->
        <svg v-if="drawingEdge" class="ivr-edge-preview" :width="canvasW" :height="canvasH" style="pointer-events:none;position:absolute;top:0;left:0">
          <path :d="drawingEdgePath" fill="none" stroke="#94a3b8" stroke-width="2" stroke-dasharray="6,3" stroke-opacity="0.8" />
        </svg>

        <!-- Empty canvas hint -->
        <div v-if="!flowNodes.length" class="ivr-canvas-hint">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" style="opacity:0.2;margin-bottom:12px"><path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-16v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/></svg>
          Drag nodes from the left panel to start building your flow
        </div>
      </div>

      <!-- Right Config Panel -->
      <div class="ivr-config-panel" v-if="selectedNodeId && selectedNodeData">
        <div class="ivr-config-header" :style="{ '--node-accent': getNodeColor(selectedNodeData.ivrType) }">
          <div class="ivr-config-header-left">
            <span class="ivr-config-icon-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path :d="getNodeIconPath(selectedNodeData.ivrType)" />
              </svg>
            </span>
            <div>
              <div class="ivr-config-type">{{ getNodeLabel(selectedNodeData.ivrType) }}</div>
              <input v-model="selectedNodeData.name" class="ivr-config-name-input" placeholder="Node name" @input="onNodeConfigChange" />
            </div>
          </div>
          <div class="ivr-config-header-actions">
            <button class="ivr-config-set-start" @click="setAsStart(selectedNodeId)" :class="{ active: flowJson.start_node === selectedNodeId }" title="Set as start node">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              {{ flowJson.start_node === selectedNodeId ? 'Start' : 'Set Start' }}
            </button>
            <button class="ivr-config-delete" @click="promptDeleteNode(selectedNodeData)" title="Delete node">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </div>
        </div>

        <div class="ivr-config-body" :key="selectedNodeId" @focusin="onIvrInputFocus">

          <!-- ── Available Variables bar ─────────────────────────────────── -->
          <div class="ivr-vars-bar">
            <div class="ivr-vars-bar-toggle" @click="varsBarOpen = !varsBarOpen">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
              <span>Variables</span>
              <span class="ivr-vars-bar-count">{{ Object.keys(ivrFlowVariables).length }}</span>
              <svg class="ivr-vars-bar-chevron" :class="{ open: varsBarOpen }" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
            </div>
            <div v-show="varsBarOpen" class="ivr-vars-chips-wrap">
              <span
                v-for="(info, name) in ivrFlowVariables"
                :key="name"
                class="ivr-var-chip"
                :class="'chip-src-' + (info.source || 'set')"
                :title="(info.description || name) + '\nClick to insert into focused field'"
                @mousedown.prevent="insertVariableAtCursor(name)"
              >{{ name }}</span>
              <span v-if="!Object.keys(ivrFlowVariables).length" class="ivr-vars-empty">No variables yet</span>
              <p class="ivr-vars-insert-hint">Click chip &#8594; inserts <code>&#123;&#123;name&#125;&#125;</code> at cursor</p>
            </div>
          </div>

          <!-- description -->
          <p v-if="getNodeDescription(selectedNodeData.ivrType)" class="ivr-node-description">{{ getNodeDescription(selectedNodeData.ivrType) }}</p>

          <!-- ── Shared audio picker + TTS generator (play, menu, get_digits, get_speech, voicemail) ── -->
          <template v-if="isAudioNode">
            <div class="ivr-section-label">Audio</div>

            <!-- Library picker -->
            <div class="ivr-field-group">
              <select v-model="selectedNodeData.config.audio_file_id" :key="'audio-' + selectedNodeData.config.audio_file_id" class="ivr-select" @change="onAudioSelect">
                <option value="">— select from library —</option>
                <option v-for="f in audioFiles" :key="f.id" :value="f.id">{{ f.name }}</option>
              </select>
              <!-- Filename row -->
              <div v-if="selectedNodeData.config.audio_file_name" class="ivr-audio-selected">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/></svg>
                <span>{{ selectedNodeData.config.audio_file_name }}</span>
                <!-- Preview button: idle only -->
                <button v-if="!previewLoading && previewAudioId !== selectedNodeData.config.audio_file_id"
                  class="ivr-audio-preview-btn"
                  @click.prevent="previewAudio(selectedNodeData.config.audio_file_id)">
                  <svg width="9" height="9" viewBox="0 0 24 24" style="display:block;fill:#fff"><path d="M8 5v14l11-7z"/></svg>
                  Preview
                </button>
              </div>
              <!-- Player card: active only -->
              <div v-if="previewAudioId === selectedNodeData.config.audio_file_id" class="ivr-audio-mini-player">
                <button class="ivr-mini-playpause" @click.prevent="previewPaused ? resumePreview() : pausePreview()">
                  <svg v-if="previewPaused" width="12" height="12" viewBox="0 0 24 24" style="display:block;fill:#fff"><path d="M8 5v14l11-7z"/></svg>
                  <svg v-else width="12" height="12" viewBox="0 0 24 24" style="display:block;fill:#fff"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                </button>
                <div class="ivr-mini-progress" @click="seekPreview">
                  <div class="ivr-mini-fill" :style="{ width: previewDuration ? (previewCurrentTime / previewDuration * 100) + '%' : '0%' }"></div>
                </div>
                <span class="ivr-mini-time">{{ formatTime(previewCurrentTime) }} / {{ formatTime(previewDuration) }}</span>
                <button class="ivr-mini-stop" @click.prevent="stopPreview" title="Stop">
                  <svg width="9" height="9" viewBox="0 0 24 24" style="display:block"><path d="M6 6h12v12H6z"/></svg>
                </button>
              </div>
            </div>

            <!-- TTS Generator panel -->
            <div class="ivr-tts-panel">
              <button class="ivr-tts-toggle" @click="tts.visible = !tts.visible; upload.visible = false">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zm-1 19.93A8.002 8.002 0 0 1 4 13H2a10 10 0 0 0 9 9.93V23h2v-1.07A10 10 0 0 0 22 13h-2a8.002 8.002 0 0 1-7 6.93z"/></svg>
                {{ tts.visible ? 'Cancel' : 'Generate with AI' }}
                <svg class="ivr-tts-chevron" :class="{ open: tts.visible }" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
              </button>

              <div v-if="tts.visible" class="ivr-tts-form">
                <!-- Provider tabs -->
                <div class="ivr-tts-tabs">
                  <button :class="['ivr-tts-tab', { active: tts.provider === 'openai' }]"
                    @click="tts.provider = 'openai'; tts.voice = 'alloy'">
                    OpenAI
                  </button>
                  <button :class="['ivr-tts-tab', { active: tts.provider === 'elevenlabs' }]"
                    @click="tts.provider = 'elevenlabs'; tts.voice = ''">
                    ElevenLabs
                  </button>
                </div>

                <!-- Voice -->
                <div class="ivr-field-group">
                  <label>Voice</label>
                  <select v-if="tts.provider === 'openai'" v-model="tts.voice" class="ivr-select">
                    <option value="alloy">Alloy — neutral</option>
                    <option value="echo">Echo — male</option>
                    <option value="fable">Fable — British</option>
                    <option value="onyx">Onyx — deep male</option>
                    <option value="nova">Nova — female</option>
                    <option value="shimmer">Shimmer — soft female</option>
                  </select>
                  <input v-else v-model="tts.voice" class="ivr-input" placeholder="Voice ID  e.g. Rachel, Josh, Domi" />
                </div>

                <!-- Script text -->
                <div class="ivr-field-group">
                  <label>Script</label>
                  <textarea v-model="tts.text" class="ivr-textarea" rows="3"
                    placeholder="Type what the caller should hear…" />
                </div>

                <p v-if="tts.error" class="ivr-tts-error">{{ tts.error }}</p>

                <button class="ivr-tts-generate-btn"
                  @click="generateTtsInline"
                  :disabled="tts.generating || !tts.text.trim() || (tts.provider === 'elevenlabs' && !tts.voice.trim())">
                  <svg v-if="tts.generating" class="spin" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/></svg>
                  <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/></svg>
                  {{ tts.generating ? 'Generating…' : 'Generate & Save to Library' }}
                </button>
              </div>
            </div>

            <!-- Upload panel -->
            <div class="ivr-tts-panel">
              <button class="ivr-tts-toggle" @click="upload.visible = !upload.visible; tts.visible = false">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                {{ upload.visible ? 'Cancel' : 'Upload File' }}
                <svg class="ivr-tts-chevron" :class="{ open: upload.visible }" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
              </button>
              <div v-if="upload.visible" class="ivr-tts-form">
                <div class="ivr-field-group">
                  <label>Name <span style="color:#6b7080;font-weight:400">(optional)</span></label>
                  <input v-model="upload.name" class="ivr-input" placeholder="Defaults to filename" />
                </div>
                <div class="ivr-field-group">
                  <div
                    class="ivr-drop-zone"
                    :class="{ 'drag-over': upload.dragging, 'has-file': !!upload.file }"
                    @dragenter.prevent="upload.dragging = true"
                    @dragover.prevent="upload.dragging = true"
                    @dragleave.prevent="upload.dragging = false"
                    @drop.prevent="onUploadDrop"
                    @click="$refs.uploadFileInput.click()"
                  >
                    <input
                      ref="uploadFileInput"
                      type="file"
                      accept=".mp3,.wav,audio/mpeg,audio/wav"
                      style="display:none"
                      @change="onUploadFileSelect"
                    />
                    <template v-if="!upload.file">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" class="ivr-drop-icon"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                      <span class="ivr-drop-text">Drop MP3 / WAV here</span>
                      <span class="ivr-drop-hint">or click to browse</span>
                    </template>
                    <template v-else>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="color:#22c55e;flex-shrink:0"><path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/></svg>
                      <div class="ivr-drop-file-name">{{ upload.file.name }}</div>
                      <span class="ivr-drop-hint">{{ (upload.file.size / 1024).toFixed(0) }} KB · click to change</span>
                    </template>
                  </div>
                </div>
                <p v-if="upload.error" class="ivr-tts-error">{{ upload.error }}</p>
                <button
                  class="ivr-tts-generate-btn"
                  @click="uploadAudioInline"
                  :disabled="upload.uploading || !upload.file"
                >
                  <svg v-if="upload.uploading" class="spin" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/></svg>
                  <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                  {{ upload.uploading ? 'Uploading…' : 'Upload & Use' }}
                </button>
              </div>
            </div>
          </template>

          <!-- ── ivr_menu — timing + routing ── -->
          <template v-if="selectedNodeData.ivrType === 'ivr_menu'">
            <div class="ivr-section-label">Timing</div>
            <div class="ivr-field-row">
              <div class="ivr-field-group">
                <label>Timeout (s)</label>
                <input v-model.number="selectedNodeData.config.timeout" type="number" class="ivr-input" min="1" @input="onNodeConfigChange" />
              </div>
              <div class="ivr-field-group">
                <label>Retries</label>
                <input v-model.number="selectedNodeData.config.retries" type="number" class="ivr-input" min="0" @input="onNodeConfigChange" />
              </div>
            </div>
            <div class="ivr-section-label">Key Routes
              <span class="ivr-section-hint">each key press creates an output wire on the node</span>
            </div>

            <!-- Configured routes -->
            <div class="ivr-routes-table">
              <!-- Committed rows -->
              <div
                v-for="key in Object.keys(selectedNodeData.routes)"
                :key="key"
                class="ivr-route-row"
              >
                <div class="ivr-route-key-cell">
                  <span class="ivr-route-key">{{ key === '_default' ? '*' : key === '_timeout' ? '⏱' : key }}</span>
                  <span class="ivr-route-key-desc">{{ key === '_default' ? 'Any other key' : key === '_timeout' ? 'No input' : 'Press ' + key }}</span>
                </div>
                <span class="ivr-route-wire-hint">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M21 11l-6-6v5H8c-2.76 0-5 2.24-5 5v4h2v-4c0-1.65 1.35-3 3-3h7v5l6-6z"/></svg>
                  {{ selectedNodeData.routes[key] ? 'wired' : 'drag to connect' }}
                </span>
                <button class="ivr-route-remove-btn" @click="removeMenuRoute(key)" title="Remove">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>

              <!-- Pending (unsaved) rows -->
              <div v-for="(row, idx) in pendingRoutes" :key="'pending-' + idx" class="ivr-route-row ivr-route-row-pending">
                <select v-model="row.key" class="ivr-select ivr-select-sm" style="flex:1" @change="tryCommitPending(idx)">
                  <option value="">— pick key —</option>
                  <optgroup label="Digit keys">
                    <option v-for="k in availableKeysForPending(idx).filter(k => !k.startsWith('_'))" :key="k" :value="k">{{ k }}</option>
                  </optgroup>
                  <optgroup label="Fallback">
                    <option v-if="availableKeysForPending(idx).includes('_default')" value="_default">Any other key</option>
                    <option v-if="availableKeysForPending(idx).includes('_timeout')" value="_timeout">No input / timeout</option>
                  </optgroup>
                </select>
                <button class="ivr-route-remove-btn" @click="removePendingRoute(idx)" title="Cancel">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>

              <div v-if="!Object.keys(selectedNodeData.routes || {}).length && !pendingRoutes.length" class="ivr-routes-empty">
                No keys yet. Click "Add Key" — then drag from the output port on the node to connect.
              </div>
            </div>

            <!-- Add Key button -->
            <button class="ivr-add-key-btn" @click="addPendingRoute" :disabled="availableMenuKeys.length === 0">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style="display:block"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
              Add Key
            </button>
          </template>

          <!-- ── ivr_collect_input — variable + input settings ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_collect_input'">
            <div class="ivr-section-label">Collection Settings</div>
            <div class="ivr-field-group">
              <label>Variable name <span style="color:#EF4444">*</span></label>
              <input
                v-model="selectedNodeData.config.variable"
                class="ivr-input"
                placeholder="caller_phone"
                pattern="[a-zA-Z_][a-zA-Z0-9_]*"
                @input="onNodeConfigChange"
              />
              <span class="ivr-field-hint">Stored as <code>&#123;&#123;{{ selectedNodeData.config.variable || 'variable' }}&#125;&#125;</code> in downstream nodes</span>
            </div>
            <div class="ivr-field-row">
              <div class="ivr-field-group">
                <label>Expected digits</label>
                <input v-model.number="selectedNodeData.config.num_digits" type="number" class="ivr-input" min="1" max="20" @input="onNodeConfigChange" />
                <span class="ivr-field-hint">Proceeds immediately once this many digits are entered</span>
              </div>
              <div class="ivr-field-group">
                <label>Inter-digit timeout (s)</label>
                <input v-model.number="selectedNodeData.config.timeout" type="number" class="ivr-input" min="3" max="30" @input="onNodeConfigChange" />
                <span class="ivr-field-hint">Max wait between keystrokes</span>
              </div>
              <div class="ivr-field-group">
                <label>Retries</label>
                <input v-model.number="selectedNodeData.config.retries" type="number" class="ivr-input" min="1" max="5" @input="onNodeConfigChange" />
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Terminate on</label>
              <input
                v-model="selectedNodeData.config.terminate_on"
                class="ivr-input"
                maxlength="1"
                placeholder="#"
                style="width:60px"
                @input="onNodeConfigChange"
              />
              <span class="ivr-field-hint">Digit that confirms early entry (stripped from result)</span>
            </div>
            <div class="ivr-vars-set" v-if="selectedNodeData.config.variable">
              <div class="ivr-vars-label">Sets after input</div>
              <div class="ivr-var-chips">
                <span class="ivr-var-chip">&#123;&#123;{{ selectedNodeData.config.variable }}&#125;&#125;</span>
              </div>
            </div>
          </template>

          <!-- ── ivr_get_speech — recording settings ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_get_speech'">
            <div class="ivr-section-label">Recording Settings</div>
            <div class="ivr-field-row">
              <div class="ivr-field-group">
                <label>Silence (s)</label>
                <input v-model.number="selectedNodeData.config.silence_sec" type="number" class="ivr-input" min="1" @input="onNodeConfigChange" />
              </div>
              <div class="ivr-field-group">
                <label>Max (s)</label>
                <input v-model.number="selectedNodeData.config.max_sec" type="number" class="ivr-input" min="5" @input="onNodeConfigChange" />
              </div>
            </div>
            <div class="ivr-vars-set">
              <div class="ivr-vars-label">Sets variable after transcription</div>
              <div class="ivr-var-chips">
                <span class="ivr-var-chip">&#123;&#123;speech_text&#125;&#125;</span>
              </div>
            </div>
          </template>

          <!-- ── ivr_record_voicemail — recording limits ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_record_voicemail'">
            <div class="ivr-section-label">Recording Limits</div>
            <div class="ivr-field-row">
              <div class="ivr-field-group">
                <label>Silence (s)</label>
                <input v-model.number="selectedNodeData.config.silence_sec" type="number" class="ivr-input" @input="onNodeConfigChange" />
              </div>
              <div class="ivr-field-group">
                <label>Max (s)</label>
                <input v-model.number="selectedNodeData.config.max_sec" type="number" class="ivr-input" @input="onNodeConfigChange" />
              </div>
            </div>
          </template>

          <!-- ── ivr_wait ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_wait'">
            <div class="ivr-section-label">Configuration</div>
            <div class="ivr-field-group">
              <label>Duration (seconds)</label>
              <input v-model.number="selectedNodeData.config.duration" type="number" class="ivr-input" min="1" @input="onNodeConfigChange" />
            </div>
          </template>

          <!-- ── ivr_transfer ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_transfer'">
            <div class="ivr-section-label">Destination</div>
            <div class="ivr-field-group">
              <label>Extension</label>
              <select
                class="ivr-select"
                :value="extensionIsVariable ? '' : selectedNodeData.config.extension"
                @change="e => { selectedNodeData.config.extension = e.target.value; onNodeConfigChange() }"
              >
                <option value="">— select extension —</option>
                <optgroup v-if="agentExtensions.length" label="Agents">
                  <option v-for="a in agentExtensions" :key="a.agent_id" :value="a.extension">
                    {{ a.name }} — {{ a.extension }}
                  </option>
                </optgroup>
                <optgroup v-if="extensions.length" label="PBX Extensions">
                  <option v-for="ext in extensions" :key="ext.extension" :value="ext.extension">
                    {{ ext.display_name ? ext.display_name + ' — ' : '' }}{{ ext.extension }}
                  </option>
                </optgroup>
              </select>
              <div class="ivr-transfer-var-row">
                <span class="ivr-var-hint">or variable:</span>
                <input
                  :value="extensionIsVariable ? selectedNodeData.config.extension : ''"
                  class="ivr-input ivr-input-sm"
                  placeholder="{{agent_ext}}"
                  @input="e => { selectedNodeData.config.extension = e.target.value; onNodeConfigChange() }"
                />
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Context</label>
              <input v-model="selectedNodeData.config.context" class="ivr-input" placeholder="default" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Timeout (s)</label>
              <input v-model.number="selectedNodeData.config.timeout" type="number" class="ivr-input" @input="onNodeConfigChange" />
            </div>
          </template>

          <!-- ── ivr_queue ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_queue'">
            <div class="ivr-section-label">Queue Assignment</div>
            <div class="ivr-field-group">
              <label>Queue</label>
              <select v-model="selectedNodeData.config.queue_name" class="ivr-select" @change="onNodeConfigChange">
                <option value="">— select queue —</option>
                <option v-for="q in queues" :key="q.id" :value="q.name">{{ q.display_name || q.name }} ({{ q.name }})</option>
              </select>
              <input
                v-if="!queues.length"
                v-model="selectedNodeData.config.queue_name"
                class="ivr-input"
                style="margin-top:8px"
                placeholder="Queue number e.g. 3100"
                @input="onNodeConfigChange"
              />
            </div>
          </template>

          <!-- ── ivr_end ── (description only) -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_end'">
            <div class="ivr-end-icon-wrap">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" style="color:#EF4444;opacity:0.7"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/></svg>
              <span>No configuration needed.</span>
            </div>
          </template>

          <!-- ── ivr_condition ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_condition'">
            <div class="ivr-section-label">Condition</div>
            <div class="ivr-field-group">
              <label>Left Operand <span class="ivr-var-hint">supports &#123;&#123;variable&#125;&#125;</span></label>
              <input v-model="selectedNodeData.config.left" class="ivr-input" placeholder="{{customer_found}}" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Operator</label>
              <select v-model="selectedNodeData.config.operator" class="ivr-select" @change="onNodeConfigChange">
                <option v-for="op in conditionOperators" :key="op" :value="op">{{ op }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Right Value</label>
              <input v-model="selectedNodeData.config.right" class="ivr-input" placeholder="true" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-section-label">Output Routes</div>
            <div class="ivr-output-routes">
              <div class="ivr-output-route ivr-output-true">
                <span class="ivr-output-route-key">true</span>
                <span class="ivr-output-route-desc">Condition matched</span>
              </div>
              <div class="ivr-output-route ivr-output-false">
                <span class="ivr-output-route-key">false</span>
                <span class="ivr-output-route-desc">Condition not matched</span>
              </div>
            </div>
          </template>

          <!-- ── ivr_set_variable ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_set_variable'">
            <div class="ivr-section-label">Assignment</div>
            <div class="ivr-field-group">
              <label>Variable Name</label>
              <input v-model="selectedNodeData.config.variable" class="ivr-input" placeholder="department" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Value <span class="ivr-var-hint">supports &#123;&#123;variable&#125;&#125;</span></label>
              <input v-model="selectedNodeData.config.value" class="ivr-input" placeholder="sales" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-vars-set" v-if="selectedNodeData.config.variable">
              <div class="ivr-vars-label">Sets variable</div>
              <div class="ivr-var-chips">
                <span class="ivr-var-chip">&#123;&#123;{{ selectedNodeData.config.variable }}&#125;&#125;</span>
              </div>
            </div>
          </template>

          <!-- ── ivr_goto ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_goto'">
            <p class="ivr-node-description">
              Drag the output handle on the node to the destination — the wire defines where the flow jumps.
            </p>
          </template>

          <!-- ── ivr_business_hours ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_business_hours'">
            <div class="ivr-section-label">Output Routes</div>
            <div class="ivr-output-routes">
              <div class="ivr-output-route ivr-output-true">
                <span class="ivr-output-route-key">open</span>
                <span class="ivr-output-route-desc">Within business hours</span>
              </div>
              <div class="ivr-output-route ivr-output-false">
                <span class="ivr-output-route-key">closed</span>
                <span class="ivr-output-route-desc">Outside business hours</span>
              </div>
            </div>
            <p class="ivr-config-info" style="margin-top:10px">Configure hours in Company Settings.</p>
          </template>

          <!-- ── ivr_blacklist_check ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_blacklist_check'">
            <div class="ivr-section-label">Output Routes</div>
            <div class="ivr-output-routes">
              <div class="ivr-output-route ivr-output-false">
                <span class="ivr-output-route-key">blocked</span>
                <span class="ivr-output-route-desc">Caller is on the blacklist</span>
              </div>
              <div class="ivr-output-route ivr-output-true">
                <span class="ivr-output-route-key">allowed</span>
                <span class="ivr-output-route-desc">Caller is not blacklisted</span>
              </div>
            </div>
            <p class="ivr-config-info" style="margin-top:10px">Manage entries under Cloud Call Center → Blacklist.</p>
          </template>

          <!-- ── ivr_lookup_customer ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_lookup_customer'">
            <div class="ivr-section-label">Output Routes</div>
            <div class="ivr-output-routes">
              <div class="ivr-output-route ivr-output-true">
                <span class="ivr-output-route-key">found</span>
                <span class="ivr-output-route-desc">Customer exists in CRM</span>
              </div>
              <div class="ivr-output-route ivr-output-false">
                <span class="ivr-output-route-key">not_found</span>
                <span class="ivr-output-route-desc">No matching record</span>
              </div>
            </div>
            <div class="ivr-vars-set">
              <div class="ivr-vars-label">Sets variables on "found"</div>
              <div class="ivr-var-chips">
                <span class="ivr-var-chip">&#123;&#123;customer_name&#125;&#125;</span>
                <span class="ivr-var-chip">&#123;&#123;customer_id&#125;&#125;</span>
                <span class="ivr-var-chip">&#123;&#123;customer_found&#125;&#125;</span>
              </div>
            </div>
          </template>

          <!-- ── ivr_assign_tag ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_assign_tag'">
            <div class="ivr-section-label">Configuration</div>
            <div class="ivr-field-group">
              <label>Tag ID</label>
              <input v-model.number="selectedNodeData.config.tag_id" type="number" class="ivr-input" placeholder="7" @input="onNodeConfigChange" />
            </div>
          </template>

          <!-- ── ivr_create_note ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_create_note'">
            <div class="ivr-section-label">Note Content</div>
            <div class="ivr-field-group">
              <label>Text <span class="ivr-var-hint">supports &#123;&#123;variable&#125;&#125;</span></label>
              <textarea v-model="selectedNodeData.config.text" class="ivr-textarea" rows="3" placeholder="Customer called via IVR. Selected: {{department}}" @input="onNodeConfigChange"></textarea>
            </div>
          </template>

          <!-- ── ivr_send_whatsapp ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_send_whatsapp'">
            <div class="ivr-section-label">WhatsApp Template</div>
            <div class="ivr-field-group">
              <label>Template <span style="color:#e53e3e">*</span></label>
              <select v-model="selectedNodeData.config.template_name" class="ivr-select" @change="onSendTemplateSelected(selectedNodeData)">
                <option value="">— select an approved template —</option>
                <option v-for="tpl in approvedTemplates" :key="tpl.id" :value="tpl.name">{{ tpl.name }} · {{ tpl.language }}</option>
              </select>
              <small v-if="approvedTemplates.length === 0" style="color:#e67e22;font-size:11px;">No approved templates found. Connect a WhatsApp account and ensure you have at least one APPROVED template.</small>
            </div>
            <!-- Template component preview -->
            <div v-if="selectedNodeData.config.template_name && (selectedNodeData.config.template_components || []).length" style="background:var(--bg-primary,#f8fafc);border:1px solid var(--border,#e5e7eb);border-radius:8px;padding:8px 10px;margin-bottom:10px;">
              <div v-for="comp in selectedNodeData.config.template_components" :key="comp.type" style="display:flex;gap:8px;align-items:flex-start;margin-bottom:4px;">
                <span style="font-size:10px;font-weight:700;color:var(--text-muted,#9ca3af);min-width:60px;text-transform:uppercase;">{{ comp.type }}</span>
                <span v-if="comp.type === 'HEADER' && comp.format !== 'TEXT'" style="font-size:11px;opacity:.7;">{{ comp.format }} media</span>
                <span v-else-if="comp.type === 'BUTTONS' && comp.buttons" style="font-size:11px;opacity:.7;">{{ (comp.buttons||[]).map(b=>b.text).join(' · ') }}</span>
                <span v-else style="font-size:11px;opacity:.7;">{{ (comp.text||'').substring(0,80) }}</span>
              </div>
            </div>
            <!-- Header text params -->
            <template v-if="selectedNodeData.config.header_type === 'TEXT' && (selectedNodeData.config.header_params||[]).length > 0">
              <div class="ivr-section-label">Header Variables</div>
              <div v-for="(param, idx) in selectedNodeData.config.header_params" :key="'hp'+idx" class="ivr-field-group">
                <label>Header &#123;&#123;{{ idx+1 }}&#125;&#125;</label>
                <input v-model="selectedNodeData.config.header_params[idx].value" class="ivr-input" placeholder="static or {{variable}}" @input="onNodeConfigChange" />
              </div>
            </template>
            <!-- Header media -->
            <template v-if="['IMAGE','VIDEO','DOCUMENT'].includes(selectedNodeData.config.header_type)">
              <div class="ivr-section-label">Header Media</div>
              <div class="ivr-field-group">
                <label>{{ selectedNodeData.config.header_type }} URL <span style="color:#e53e3e">*</span></label>
                <input v-model="selectedNodeData.config.header_media_url" class="ivr-input" placeholder="https://... or {{variable_with_url}}" @input="onNodeConfigChange" />
              </div>
            </template>
            <!-- Body params -->
            <template v-if="(selectedNodeData.config.body_params||[]).length > 0">
              <div class="ivr-section-label">Body Variables</div>
              <div v-for="(param, idx) in selectedNodeData.config.body_params" :key="'bp'+idx" class="ivr-field-group">
                <label>Body &#123;&#123;{{ idx+1 }}&#125;&#125;</label>
                <input v-model="selectedNodeData.config.body_params[idx].value" class="ivr-input" placeholder="static or {{variable}}" @input="onNodeConfigChange" />
              </div>
            </template>
            <!-- Button URL params -->
            <template v-if="(selectedNodeData.config.button_params||[]).length > 0">
              <div class="ivr-section-label">Button URL Variables</div>
              <div v-for="(btnParam, idx) in selectedNodeData.config.button_params" :key="'btp'+idx" class="ivr-field-group">
                <label>Button "{{ btnParam.button_text }}" &#123;&#123;1&#125;&#125;</label>
                <input v-model="selectedNodeData.config.button_params[idx].value" class="ivr-input" placeholder="static or {{variable}}" @input="onNodeConfigChange" />
              </div>
            </template>
            <!-- Override recipient (test/debug) -->
            <div class="ivr-section-label" style="margin-top:6px;">Recipient (optional)</div>
            <div class="ivr-field-group">
              <label>Override phone number</label>
              <input v-model="selectedNodeData.config.override_to" class="ivr-input" placeholder="e.g. 201012345678 — leave blank to use caller's number" @input="onNodeConfigChange" />
              <small class="ivr-field-hint" style="color:#d97706;">For testing only — bypasses the caller's number when set.</small>
            </div>
            <p class="ivr-config-info">Sends an approved WhatsApp template to the caller's phone number. Supports &#123;&#123;variable&#125;&#125; substitution.</p>
          </template>

          <!-- ── ivr_webhook ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_webhook'">
            <div class="ivr-section-label">Request</div>
            <div class="ivr-field-group">
              <label>URL</label>
              <input v-model="selectedNodeData.config.url" class="ivr-input" placeholder="https://..." @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Method</label>
              <select v-model="selectedNodeData.config.method" class="ivr-select" @change="onNodeConfigChange">
                <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option>
              </select>
            </div>
          </template>

          <!-- ── api_call ── -->
          <template v-else-if="selectedNodeData.ivrType === 'api_call'">
            <div class="ivr-field-group">
              <label>API Node Name</label>
              <input v-model="ensureApiCallConfig(selectedNodeData).api_node_name" class="ivr-input" placeholder="e.g. GetCategories, FetchUserData" @input="onNodeConfigChange" />
              <small class="ivr-field-hint">Give this API call a unique name to identify response variables</small>
            </div>

            <div class="ivr-field-group">
              <label>API URL</label>
              <VarTextEditor v-model="ensureApiCallConfig(selectedNodeData).api_url" placeholder="https://api.example.com/endpoint" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodeConfigChange" />
            </div>

            <div class="ivr-field-group">
              <label>HTTP Method</label>
              <select v-model="ensureApiCallConfig(selectedNodeData).http_method" class="ivr-select" @change="onNodeConfigChange">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <!-- Query params (GET) -->
            <div v-if="ensureApiCallConfig(selectedNodeData).http_method === 'GET'" class="ivr-field-group">
              <label>Query Parameters</label>
              <div v-for="(value, key, index) in ensureApiCallConfig(selectedNodeData).query_params" :key="'qp-' + index" class="ivr-kv-pair">
                <input :value="key" class="ivr-input ivr-kv-key" placeholder="name" @blur="updateQueryParam(selectedNodeData, key, $event.target.value, value)" />
                <VarTextEditor :modelValue="ensureApiCallConfig(selectedNodeData).query_params[key]" :single-line="true" placeholder="value or {{var}}" class="ivr-kv-vte" @focus="activeVarEditor = $event" @update:modelValue="val => { ensureApiCallConfig(selectedNodeData).query_params[key] = val; onNodeConfigChange() }" />
                <button class="ivr-kv-remove" @click="removeQueryParam(selectedNodeData, key)">×</button>
              </div>
              <button class="ivr-add-row-btn" @click="addQueryParam(selectedNodeData)">+ Add Parameter</button>
            </div>

            <!-- Headers -->
            <div class="ivr-field-group">
              <label>Headers</label>
              <div v-for="(value, key, index) in ensureApiCallConfig(selectedNodeData).headers" :key="'hdr-' + index" class="ivr-kv-pair">
                <select :value="key" class="ivr-select ivr-kv-key" @change="updateHeaderKey(selectedNodeData, key, $event.target.value)">
                  <option value="">Select Type...</option>
                  <option value="Authorization">Authorization</option>
                  <option value="Content-Type">Content-Type</option>
                  <option value="Accept">Accept</option>
                  <option value="X-API-Key">X-API-Key</option>
                  <option value="X-Auth-Token">X-Auth-Token</option>
                  <option value="Cookie">Cookie</option>
                  <option value="User-Agent">User-Agent</option>
                  <option :value="key">{{ key }}</option>
                </select>
                <VarTextEditor :modelValue="ensureApiCallConfig(selectedNodeData).headers[key]" :single-line="true" placeholder="e.g. Bearer {{token}}" class="ivr-kv-vte" @focus="activeVarEditor = $event" @update:modelValue="val => { ensureApiCallConfig(selectedNodeData).headers[key] = val; onNodeConfigChange() }" />
                <button class="ivr-kv-remove" @click="removeHeader(selectedNodeData, key)">×</button>
              </div>
              <button class="ivr-add-row-btn" @click="addHeader(selectedNodeData)">+ Add Header</button>
            </div>

            <!-- Body format (POST/PUT/PATCH) -->
            <div v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNodeData).http_method)" class="ivr-field-group">
              <label>Body Format</label>
              <select v-model="ensureApiCallConfig(selectedNodeData).body_format" class="ivr-select" @change="onNodeConfigChange">
                <option value="json">JSON (application/json)</option>
                <option value="form">Form URL-Encoded</option>
                <option value="form-data">Form Data (multipart)</option>
              </select>
            </div>

            <!-- JSON body -->
            <div v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNodeData).http_method) && ensureApiCallConfig(selectedNodeData).body_format === 'json'" class="ivr-field-group">
              <label>Request Body <span class="ivr-var-hint">JSON</span></label>
              <VarTextEditor v-model="ensureApiCallConfig(selectedNodeData).request_body_json" placeholder='{"key": "value", "token": "{{my_token}}"}' :rows="5" @focus="activeVarEditor = $event" @update:modelValue="onNodeConfigChange" />
            </div>

            <!-- Form params -->
            <div v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNodeData).http_method) && ['form', 'form-data'].includes(ensureApiCallConfig(selectedNodeData).body_format)" class="ivr-field-group">
              <label>Form Fields</label>
              <div v-for="(value, key, index) in ensureApiCallConfig(selectedNodeData).form_params" :key="'fp-' + index" class="ivr-kv-pair">
                <input :value="key" class="ivr-input ivr-kv-key" placeholder="field name" @blur="updateFormParam(selectedNodeData, key, $event.target.value, value)" />
                <VarTextEditor :modelValue="ensureApiCallConfig(selectedNodeData).form_params[key]" :single-line="true" placeholder="value or {{var}}" class="ivr-kv-vte" @focus="activeVarEditor = $event" @update:modelValue="val => { ensureApiCallConfig(selectedNodeData).form_params[key] = val; onNodeConfigChange() }" />
                <button class="ivr-kv-remove" @click="removeFormParam(selectedNodeData, key)">×</button>
              </div>
              <button class="ivr-add-row-btn" @click="addFormParam(selectedNodeData)">+ Add Field</button>
            </div>

            <!-- Request ID -->
            <div class="ivr-field-group">
              <label>Request ID (idmsg) <span class="ivr-var-hint">optional</span></label>
              <select v-model="ensureApiCallConfig(selectedNodeData).idmsg_mode" class="ivr-select" @change="onNodeConfigChange">
                <option value="off">Disabled</option>
                <option value="auto">Auto — generate UUID</option>
                <option value="custom">Custom value</option>
              </select>
            </div>
            <div v-if="ensureApiCallConfig(selectedNodeData).idmsg_mode === 'custom'" class="ivr-field-group">
              <label>Custom idmsg value</label>
              <VarTextEditor v-model="ensureApiCallConfig(selectedNodeData).idmsg_custom_value" placeholder="e.g. {{session_id}}" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodeConfigChange" />
            </div>

            <!-- SSL -->
            <div class="ivr-field-group">
              <label class="ivr-checkbox-label">
                <input type="checkbox" v-model="ensureApiCallConfig(selectedNodeData).skip_ssl_verify" @change="onNodeConfigChange" />
                <span>Skip SSL certificate verification</span>
              </label>
            </div>

            <!-- Variable picker -->
            <div v-if="Object.keys(ivrFlowVariables).length" class="ivr-api-var-picker">
              <div class="ivr-api-var-header">
                <span>Insert variable</span>
                <input v-model="apiVarSearch" class="ivr-api-var-search" placeholder="Search..." @keydown.esc="apiVarSearch = ''" />
              </div>
              <div class="ivr-api-var-chips">
                <span v-for="(info, varName) in filteredApiVars" :key="varName" class="ivr-var-chip-pick" :title="info.description || varName" @mousedown.prevent="insertVariableAtCursor(varName)">{{ varName }}</span>
              </div>
            </div>

            <!-- Response mapping -->
            <div class="ivr-field-group">
              <label>Response Variable Mapping</label>
              <small class="ivr-field-hint">Map JSON response keys to flow variables</small>
              <div v-for="(varName, jsonPath, index) in ensureApiCallConfig(selectedNodeData).response_mapping" :key="'rm-' + index" class="ivr-kv-pair">
                <input :value="jsonPath" class="ivr-input ivr-kv-key" placeholder="JSON path (e.g. data.token)" @blur="updateResponseMapping(selectedNodeData, jsonPath, $event.target.value, varName)" />
                <input v-model="ensureApiCallConfig(selectedNodeData).response_mapping[jsonPath]" class="ivr-input ivr-kv-val" placeholder="variable name" @input="onNodeConfigChange" />
                <button class="ivr-kv-remove" @click="removeResponseMapping(selectedNodeData, jsonPath)">×</button>
              </div>
              <button class="ivr-add-row-btn" @click="addResponseMapping(selectedNodeData)">+ Add Mapping</button>
            </div>

            <!-- Test button -->
            <div class="ivr-field-group">
              <button class="ivr-tts-generate-btn" @click="prepareApiTest(selectedNodeData)" :disabled="testingApi">
                <svg v-if="testingApi" class="spin" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/></svg>
                {{ testingApi ? 'Testing...' : '🧪 Test API Call' }}
              </button>
            </div>

            <!-- Sample values for variables in request -->
            <div v-if="apiTestVarsDetected.length > 0" class="ivr-api-test-samples">
              <div class="ivr-api-test-samples-header">
                <span>Variable sample values</span>
                <small>Replaces {{variable}} placeholders for this test only</small>
              </div>
              <div v-for="varName in apiTestVarsDetected" :key="varName" class="ivr-api-test-sample-row">
                <span class="ivr-api-test-tag">{{ varName }}</span>
                <input v-model="testSampleValues[varName]" class="ivr-input" :placeholder="'sample value for ' + varName" />
              </div>
              <button class="ivr-tts-generate-btn" @click="runApiTest(selectedNodeData)" :disabled="testingApi" style="margin-top:8px;width:100%">
                {{ testingApi ? 'Testing...' : '▶ Run Test' }}
              </button>
            </div>

            <!-- Test response -->
            <div v-if="apiTestResponse && apiTestNodeId === selectedNodeData.id" class="ivr-field-group">
              <label>Test Response</label>
              <pre class="ivr-api-response-pre">{{ JSON.stringify(apiTestResponse, null, 2) }}</pre>
            </div>
            <div v-if="apiTestError && apiTestNodeId === selectedNodeData.id" class="ivr-tts-error">{{ apiTestError }}</div>
          </template>

          <!-- ── ivr_ai_response ── -->
          <template v-else-if="selectedNodeData.ivrType === 'ivr_ai_response'">
            <div class="ivr-section-label">Prompt</div>
            <div class="ivr-field-group">
              <label>Prompt text <span class="ivr-var-hint">supports &#123;&#123;variable&#125;&#125;</span></label>
              <textarea v-model="selectedNodeData.config.prompt" class="ivr-textarea" rows="4" placeholder="The customer said: {{speech_text}}. Reply in one sentence." @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-section-label">Output</div>
            <div class="ivr-field-group">
              <label>Save result to variable</label>
              <input v-model="selectedNodeData.config.save_to" class="ivr-input" placeholder="ai_reply" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-vars-set" v-if="selectedNodeData.config.save_to">
              <div class="ivr-vars-label">Sets variable</div>
              <div class="ivr-var-chips">
                <span class="ivr-var-chip">&#123;&#123;{{ selectedNodeData.config.save_to }}&#125;&#125;</span>
              </div>
            </div>
          </template>

          <!-- ── condition (multi-condition) ── -->
          <template v-else-if="selectedNodeData.ivrType === 'condition'">
            <div class="ivr-section-label">Conditions <small style="font-weight:400;opacity:0.7">(top-to-bottom, first match wins)</small></div>
            <div v-for="(cond, index) in (selectedNodeData.config.conditions || [])" :key="cond.id" class="condition-rule-row">
              <div class="condition-rule-header">
                <span class="condition-rule-badge">IF {{ index + 1 }}</span>
                <button v-if="(selectedNodeData.config.conditions||[]).length > 1" @click="removeConditionForNode(selectedNodeData, index)" class="btn-remove-condition" title="Remove">×</button>
              </div>
              <div class="condition-rule-fields">
                <input v-model="cond.variable" class="ivr-input" placeholder="{{variable_name}}" @input="onNodeConfigChange" />
                <select v-model="cond.operator" class="ivr-select" @change="onNodeConfigChange">
                  <option value="==">== equals</option>
                  <option value="!=">!= not equals</option>
                  <option value=">">&gt; greater than</option>
                  <option value="<">&lt; less than</option>
                  <option value=">=">&gt;= greater or equal</option>
                  <option value="<=">&lt;= less or equal</option>
                  <option value="contains">contains</option>
                  <option value="not_contains">not contains</option>
                  <option value="starts_with">starts with</option>
                  <option value="ends_with">ends with</option>
                  <option value="is_empty">is empty</option>
                  <option value="is_not_empty">is not empty</option>
                </select>
                <input v-if="!['is_empty','is_not_empty'].includes(cond.operator)" v-model="cond.value" class="ivr-input" placeholder="value" @input="onNodeConfigChange" />
              </div>
              <div class="condition-port-hint">Drag the <strong>IF {{ index + 1 }}</strong> port on the node to connect</div>
            </div>
            <div class="condition-rule-row condition-else-row">
              <div class="condition-rule-header"><span class="condition-rule-badge else-badge">ELSE</span></div>
              <div class="condition-port-hint">Drag the <strong>Else</strong> port to the fallback node</div>
            </div>
            <button @click="addConditionForNode(selectedNodeData)" class="ivr-btn-add" style="margin-top:8px">+ Add Condition</button>
          </template>

          <!-- ── calculator ── -->
          <template v-else-if="selectedNodeData.ivrType === 'calculator'">
            <div class="ivr-section-label">Calculations</div>
            <div class="calc-cards">
              <div v-for="(calc, idx) in (selectedNodeData.config.calculations || [])" :key="idx" class="calc-card">
                <div class="calc-eq-row">
                  <input v-model="calc.operand_a" class="calc-operand" placeholder="{{var}} or number" @focus="activeCalcInput={idx,field:'a'}" @input="onNodeConfigChange" />
                  <select v-model="calc.operator" class="calc-op-select" @change="onNodeConfigChange">
                    <optgroup label="Arithmetic">
                      <option value="+">＋ Add</option>
                      <option value="-">－ Subtract</option>
                      <option value="*">× Multiply</option>
                      <option value="/">÷ Divide</option>
                      <option value="%">% Remainder</option>
                    </optgroup>
                    <optgroup label="Rounding">
                      <option value="round">↻ Round (B=decimals)</option>
                      <option value="floor">⌊⌋ Round Down</option>
                      <option value="ceil">⌈⌉ Round Up</option>
                      <option value="abs">|x| Absolute value</option>
                    </optgroup>
                    <optgroup label="Compare">
                      <option value="min">↓ Keep smallest</option>
                      <option value="max">↑ Keep largest</option>
                    </optgroup>
                  </select>
                  <input v-if="calcOpNeedsB(calc.operator)" v-model="calc.operand_b" class="calc-operand" :placeholder="calc.operator==='round'?'decimals (0)':'{{var}} or number'" @focus="activeCalcInput={idx,field:'b'}" @input="onNodeConfigChange" />
                </div>
                <div class="calc-result-row">
                  <span class="calc-save-label">save as</span>
                  <input v-model="calc.result_variable" class="calc-result-input" placeholder="result_variable" @input="onNodeConfigChange" />
                  <button class="calc-remove-btn" type="button" @click="removeCalcRow(selectedNodeData, idx)" title="Remove">✕</button>
                </div>
              </div>
              <div v-if="!(selectedNodeData.config.calculations || []).length" class="calc-empty">Click "+ Add" to create your first calculation</div>
            </div>
            <button class="ivr-btn-add" type="button" @click="addCalcRow(selectedNodeData)" style="margin-top:8px">＋ Add Calculation</button>
            <small class="ivr-config-info">Rows run top to bottom — you can use a result from row 1 in row 2.</small>
          </template>

          <!-- ── delay ── -->
          <template v-else-if="selectedNodeData.ivrType === 'delay'">
            <div class="ivr-section-label">Wait Duration</div>
            <div class="ivr-field-group">
              <label>Wait for</label>
              <div class="delay-input-row">
                <input v-model.number="selectedNodeData.config.delay_value" type="number" min="1" class="ivr-input delay-value-input" @input="onNodeConfigChange" />
                <select v-model="selectedNodeData.config.delay_unit" class="ivr-select delay-unit-select" @change="onNodeConfigChange">
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
            </div>
            <p class="ivr-config-info">Pauses the flow for the set duration before continuing to the next node.</p>
          </template>

          <!-- ── current_datetime ── -->
          <template v-else-if="selectedNodeData.ivrType === 'current_datetime'">
            <div class="ivr-section-label">Date &amp; Time Capture</div>
            <div class="ivr-field-group">
              <label>Variable prefix <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.variable_prefix" class="ivr-input" placeholder="now" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7;margin-top:4px;display:block;">Outputs: &#123;&#123;{{ selectedNodeData.config.variable_prefix||'now' }}_date&#125;&#125; &#123;&#123;{{ selectedNodeData.config.variable_prefix||'now' }}_time&#125;&#125; &#123;&#123;{{ selectedNodeData.config.variable_prefix||'now' }}_day&#125;&#125; etc.</small>
            </div>
            <div class="ivr-field-group">
              <label>Day offset</label>
              <div style="display:flex;align-items:center;gap:10px">
                <input v-model.number="selectedNodeData.config.offset_days" type="number" class="ivr-input" style="width:90px" placeholder="0" @input="onNodeConfigChange" />
                <span style="font-size:12px;opacity:.6;font-style:italic">{{ offsetDayLabel(selectedNodeData.config.offset_days) }}</span>
              </div>
              <small style="font-size:11px;opacity:.7">0 = today · 1 = tomorrow · -1 = yesterday</small>
            </div>
            <div class="ivr-field-group">
              <label class="ivr-checkbox-label">
                <input type="checkbox" v-model="selectedNodeData.config.skip_weekends" @change="onSkipWeekendsToggle(selectedNodeData); onNodeConfigChange()" />
                <span>Skip certain days</span>
              </label>
            </div>
            <div v-if="selectedNodeData.config.skip_weekends" class="ivr-field-group">
              <label>Days to skip</label>
              <div class="weekend-days-picker">
                <label v-for="day in [{n:'Mon',v:1},{n:'Tue',v:2},{n:'Wed',v:3},{n:'Thu',v:4},{n:'Fri',v:5},{n:'Sat',v:6},{n:'Sun',v:0}]" :key="day.v" class="weekend-day-chip" :class="{ active: Array.isArray(selectedNodeData.config.weekend_days) && selectedNodeData.config.weekend_days.map(Number).includes(day.v) }">
                  <input type="checkbox" :value="day.v" :checked="Array.isArray(selectedNodeData.config.weekend_days) && selectedNodeData.config.weekend_days.map(Number).includes(day.v)" @change="toggleWeekendDay(selectedNodeData, day.v, $event)" />
                  {{ day.n }}
                </label>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Timezone</label>
              <select v-model="selectedNodeData.config.timezone" class="ivr-select" @change="onNodeConfigChange">
                <option value="UTC">UTC</option>
                <option value="Etc/GMT-3">Cairo — Summer (UTC+3)</option>
                <option value="Africa/Cairo">Cairo — Winter (UTC+2)</option>
                <option value="Africa/Johannesburg">Africa/Johannesburg (UTC+2)</option>
                <option value="Africa/Lagos">Africa/Lagos (UTC+1)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (UTC+3)</option>
                <option value="America/New_York">America/New_York (ET)</option>
                <option value="America/Chicago">America/Chicago (CT)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
                <option value="Asia/Riyadh">Asia/Riyadh (UTC+3)</option>
                <option value="Asia/Beirut">Asia/Beirut (EET)</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                <option value="Europe/Moscow">Europe/Moscow (UTC+3)</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Date format</label>
              <select v-model="selectedNodeData.config.date_format" class="ivr-select" @change="onNodeConfigChange">
                <option value="d/m/Y">DD/MM/YYYY</option>
                <option value="m/d/Y">MM/DD/YYYY</option>
                <option value="Y-m-d">YYYY-MM-DD</option>
                <option value="d M Y">DD Mon YYYY</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Time format</label>
              <select v-model="selectedNodeData.config.time_format" class="ivr-select" @change="onNodeConfigChange">
                <option value="H:i">24-hour (14:30)</option>
                <option value="H:i:s">24-hour with seconds</option>
                <option value="h:i A">12-hour AM/PM</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Output preview</label>
              <div style="background:var(--bg-primary,#f8fafc);border:1px solid var(--border,#e5e7eb);border-radius:6px;padding:8px;">
                <div v-for="row in datetimeSampleValues(selectedNodeData)" :key="row.key" style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0;">
                  <code>&#123;&#123;{{ row.key }}&#125;&#125;</code>
                  <span style="opacity:.7">{{ row.val }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- ── check_24h_window ── -->
          <template v-else-if="selectedNodeData.ivrType === 'check_24h_window'">
            <div class="ivr-section-label">24h Window Check</div>
            <div class="ivr-field-group">
              <label>Hours limit</label>
              <input v-model.number="selectedNodeData.config.hours_limit" type="number" min="1" max="168" class="ivr-input" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Default 24. The "within" port fires if the customer contacted within this many hours.</small>
            </div>
            <div class="ivr-field-group">
              <label>Save hours-since to variable <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="hours_since_last_contact" @input="onNodeConfigChange" />
            </div>
            <p class="ivr-config-info">Routes: <strong>within</strong> (contacted within limit) → <strong>outside</strong> (longer ago or never).</p>
          </template>

          <!-- ── check_working_hours ── -->
          <template v-else-if="selectedNodeData.ivrType === 'check_working_hours'">
            <div class="ivr-section-label">Working Hours Check</div>
            <div class="ivr-field-group">
              <label>Timezone</label>
              <select v-model="selectedNodeData.config.timezone" class="ivr-select" @change="onNodeConfigChange">
                <option value="UTC">UTC</option>
                <option value="Etc/GMT-3">Cairo — Summer (UTC+3)</option>
                <option value="Africa/Cairo">Cairo — Winter (UTC+2)</option>
                <option value="Africa/Lagos">Africa/Lagos (UTC+1)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (UTC+3)</option>
                <option value="America/New_York">America/New_York (ET)</option>
                <option value="America/Chicago">America/Chicago (CT)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
                <option value="Asia/Riyadh">Asia/Riyadh (UTC+3)</option>
                <option value="Asia/Beirut">Asia/Beirut (EET)</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">Europe/Paris (CET)</option>
                <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                <option value="Europe/Moscow">Europe/Moscow (UTC+3)</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Save current time to variable <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="current_time" @input="onNodeConfigChange" />
            </div>
            <p class="ivr-config-info">Routes to <strong>open</strong> if within working hours set in Company Profile, or <strong>closed</strong> otherwise.</p>
          </template>

          <!-- ── check_tag ── -->
          <template v-else-if="selectedNodeData.ivrType === 'check_tag'">
            <div class="ivr-section-label">Tag Conditions <small style="font-weight:400;opacity:0.7">(first match wins)</small></div>
            <div v-for="(cond, index) in (selectedNodeData.config.conditions || [])" :key="cond.id" class="condition-rule-row">
              <div class="condition-rule-header">
                <span class="condition-rule-badge">IF {{ index + 1 }}</span>
                <button @click="removeTagConditionForNode(selectedNodeData, index)" class="btn-remove-condition" title="Remove">×</button>
              </div>
              <div class="condition-rule-fields">
                <select v-model="cond.tag_id" class="ivr-select" @change="onNodeConfigChange">
                  <option value="">— select tag —</option>
                  <option v-for="tag in availableTags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
                </select>
              </div>
              <div class="condition-port-hint">Drag the <strong>IF {{ index + 1 }}</strong> port on the node to connect</div>
            </div>
            <div class="condition-rule-row condition-else-row">
              <div class="condition-rule-header"><span class="condition-rule-badge else-badge">ELSE</span></div>
              <div class="condition-port-hint">Drag the <strong>Else</strong> port to the fallback node</div>
            </div>
            <p v-if="!availableTags.length" class="ivr-config-info" style="color:#e67e22">No tags found. Create tags in Customer Tags first.</p>
            <button @click="addTagConditionForNode(selectedNodeData)" class="ivr-btn-add" style="margin-top:8px">+ Add Condition</button>
          </template>

          <!-- ── remove_tag ── -->
          <template v-else-if="selectedNodeData.ivrType === 'remove_tag'">
            <div class="ivr-section-label">Remove Tag</div>
            <div class="ivr-field-group">
              <label>Tag to Remove</label>
              <select v-model="selectedNodeData.config.tag_id" class="ivr-select" @change="onNodeConfigChange">
                <option :value="null">— select tag —</option>
                <option v-for="tag in availableTags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
              </select>
              <p v-if="!availableTags.length" class="ivr-config-info" style="color:#e67e22">No tags found. Create tags in Customer Tags first.</p>
            </div>
          </template>

          <!-- ── qr_generator ── -->
          <template v-else-if="selectedNodeData.ivrType === 'qr_generator'">
            <div class="ivr-section-label">QR Generator</div>
            <div class="ivr-field-group">
              <label>Data to encode</label>
              <textarea v-model="selectedNodeData.config.qr_data" class="ivr-textarea" rows="2" placeholder="https://... or {{variable}}" @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-field-group">
              <label>QR Size (px)</label>
              <div style="display:flex;gap:8px">
                <button v-for="s in [{l:'S',v:200},{l:'M',v:300},{l:'L',v:400},{l:'XL',v:500}]" :key="s.v" type="button" class="ivr-size-pill" :class="{'ivr-size-pill--active':(selectedNodeData.config.size||300)===s.v}" @click="selectedNodeData.config.size=s.v;onNodeConfigChange()">{{ s.l }} <span style="opacity:.6;font-size:10px">{{ s.v }}px</span></button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Save image URL as</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="qr_code_url" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Use &#123;&#123;{{ selectedNodeData.config.result_variable||'qr_code_url' }}&#125;&#125; in later nodes.</small>
            </div>
          </template>

          <!-- ── send_email ── -->
          <template v-else-if="selectedNodeData.ivrType === 'send_email'">
            <div class="ivr-section-label">Send Email</div>
            <div class="ivr-field-group">
              <label>To <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.to" class="ivr-input" placeholder="{{customer_email}} or name@example.com" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Subject <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.subject" class="ivr-input" placeholder="Your request has been confirmed" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label style="display:flex;align-items:center;justify-content:space-between">
                Body
                <label class="ivr-checkbox-label" style="font-weight:400;font-size:11px">
                  <input type="checkbox" v-model="selectedNodeData.config.html_mode" @change="onNodeConfigChange" />
                  <span>HTML mode</span>
                </label>
              </label>
              <textarea v-model="selectedNodeData.config.body" class="ivr-textarea" rows="6" :placeholder="selectedNodeData.config.html_mode?'<p>Hello <strong>{{customer_name}}</strong></p>':'Hello {{customer_name}}, ...'" @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-field-group">
              <label>From Name <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.from_name" class="ivr-input" placeholder="Quali-D" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Reply-To <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.reply_to" class="ivr-input" placeholder="support@yourcompany.com" @input="onNodeConfigChange" />
            </div>
            <p class="ivr-config-info">Delivered via s1notification@quali-d.com. Supports &#123;&#123;variable&#125;&#125; substitution.</p>
          </template>

          <!-- ── currency_exchange ── -->
          <template v-else-if="selectedNodeData.ivrType === 'currency_exchange'">
            <div class="ivr-section-label">Currency Exchange</div>
            <div class="ivr-field-group">
              <label>Amount <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.amount" class="ivr-input" placeholder="{{price}} or 100" @input="onNodeConfigChange" />
            </div>
            <div style="display:flex;gap:8px;align-items:flex-end">
              <div class="ivr-field-group" style="flex:1">
                <label>From</label>
                <select v-model="selectedNodeData.config.from_currency" class="ivr-select" @change="onNodeConfigChange">
                  <option v-for="c in currencyList" :key="c" :value="c">{{ c }}</option>
                </select>
              </div>
              <div style="padding-bottom:10px;opacity:.6;font-size:18px">→</div>
              <div class="ivr-field-group" style="flex:1">
                <label>To</label>
                <select v-model="selectedNodeData.config.to_currency" class="ivr-select" @change="onNodeConfigChange">
                  <option v-for="c in currencyList" :key="c" :value="c">{{ c }}</option>
                </select>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Decimal places</label>
              <input v-model.number="selectedNodeData.config.decimal_places" type="number" min="0" max="8" class="ivr-input" style="width:80px" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Result variable <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="converted_amount" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Also saves _rate and _date variables. Live rates via frankfurter.app (ECB, updated daily).</small>
            </div>
          </template>

          <!-- ── csat_rating ── -->
          <template v-else-if="selectedNodeData.ivrType === 'csat_rating'">
            <div class="ivr-section-label">CSAT Rating</div>
            <div class="ivr-field-group">
              <label>Rating Question</label>
              <textarea v-model="selectedNodeData.config.question" class="ivr-textarea" rows="2" placeholder="How would you rate your experience?" @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-field-group">
              <label>Scale</label>
              <div style="display:flex;gap:8px">
                <button type="button" class="ivr-size-pill" :class="{'ivr-size-pill--active':selectedNodeData.config.scale===5}" @click="selectedNodeData.config.scale=5;onNodeConfigChange()">1–5 ⭐</button>
                <button type="button" class="ivr-size-pill" :class="{'ivr-size-pill--active':selectedNodeData.config.scale===10}" @click="selectedNodeData.config.scale=10;onNodeConfigChange()">1–10</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Save rating to variable</label>
              <input v-model="selectedNodeData.config.variable_name" class="ivr-input" placeholder="csat_rating" @input="onNodeConfigChange" />
            </div>
            <p class="ivr-config-info">Ratings are recorded in CSAT analytics. Caller presses a digit via DTMF (IVR).</p>
          </template>

          <!-- ── todo_create ── -->
          <template v-else-if="selectedNodeData.ivrType === 'todo_create'">
            <div class="ivr-section-label">Create Todo</div>
            <div class="ivr-field-group">
              <label>Title <span style="color:#e53e3e">*</span></label>
              <input v-model="selectedNodeData.config.title" class="ivr-input" placeholder="Follow up with {{customer_name}}" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Description <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <textarea v-model="selectedNodeData.config.description" class="ivr-textarea" rows="2" placeholder="Customer called about: {{selected_option}}" @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-field-group">
              <label>Priority</label>
              <select v-model="selectedNodeData.config.priority" class="ivr-select" @change="onNodeConfigChange">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <p class="ivr-config-info">Creates a todo task linked to the caller's customer profile. Supports &#123;&#123;variable&#125;&#125; in title and description.</p>
          </template>

          <!-- ═══════════════ Google Sheets ═══════════════ -->

          <!-- Google: Not connected -->
          <template v-else-if="isGoogleNodeType(selectedNodeData.ivrType) && googleConnections.length === 0">
            <div class="ivr-section-label">Google Integration</div>
            <div style="text-align:center;padding:20px 12px">
              <p style="font-size:13px;opacity:.7;margin-bottom:12px">No Google account connected. Connect one first.</p>
              <router-link to="/settings/google" target="_blank" class="ivr-btn-add">Open Google Integrations</router-link>
            </div>
          </template>

          <!-- ── google_sheets_read ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_sheets_read'">
            <div class="ivr-section-label">Google Sheets — Read Rows</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onGsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Spreadsheet</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.spreadsheet_id" class="ivr-select" @change="onGsSpreadsheetChange" :disabled="!selectedNodeData.config.google_connection_id || gsLoadingSpreadsheets">
                  <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                  <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGsSpreadsheets(selectedNodeData.config.google_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Sheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.sheet_name" class="ivr-select" @change="onGsSheetChange" :disabled="!selectedNodeData.config.spreadsheet_id || gsLoadingSheets">
                  <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                  <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.spreadsheet_id" @click="loadGsSheetsInSpread(selectedNodeData.config.google_connection_id, selectedNodeData.config.spreadsheet_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Filter</label>
              <div class="filter-row">
                <select v-model="selectedNodeData.config.filter_column" class="ivr-select" style="flex:1" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.sheet_name">
                  <option value="">{{ gsLoadingHeaders ? 'Loading...' : (gsColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                  <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
                <select v-model="selectedNodeData.config.filter_operator" class="ivr-select" style="width:110px" @change="onNodeConfigChange">
                  <option value="==">equals</option>
                  <option value="contains">contains</option>
                  <option value="starts_with">starts with</option>
                  <option value="!=">not equals</option>
                </select>
                <input v-model="selectedNodeData.config.filter_value" class="ivr-input" placeholder="value or {{var}}" @input="onNodeConfigChange" />
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Limit rows</label>
              <input v-model.number="selectedNodeData.config.limit" class="ivr-input" type="number" min="1" max="1000" @input="onNodeConfigChange" style="width:80px" />
            </div>
            <div class="ivr-field-group">
              <label>Save result as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="sheet_rows" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Also saves _count and per-column variables from the first matched row.</small>
            </div>
            <p class="ivr-config-info">Routes: <strong>found</strong> (1+ rows match) → <strong>not_found</strong> (no rows match).</p>
          </template>

          <!-- ── google_sheets_append ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_sheets_append'">
            <div class="ivr-section-label">Google Sheets — Add Row</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onGsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Spreadsheet</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.spreadsheet_id" class="ivr-select" @change="onGsSpreadsheetChange" :disabled="!selectedNodeData.config.google_connection_id || gsLoadingSpreadsheets">
                  <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                  <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGsSpreadsheets(selectedNodeData.config.google_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Sheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.sheet_name" class="ivr-select" @change="onGsSheetChange" :disabled="!selectedNodeData.config.spreadsheet_id || gsLoadingSheets">
                  <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                  <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.spreadsheet_id" @click="loadGsSheetsInSpread(selectedNodeData.config.google_connection_id, selectedNodeData.config.spreadsheet_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                <label style="margin:0">Row Data</label>
                <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNodeData.config.sheet_name" @click="loadGsColumnHeaders(selectedNodeData.config.google_connection_id, selectedNodeData.config.spreadsheet_id, selectedNodeData.config.sheet_name)" title="Refresh columns">↻</button>
              </div>
              <div class="row-builder">
                <div v-for="(item, i) in rowDataItems" :key="i" class="row-builder-item">
                  <select v-model="item.col" class="ivr-select row-builder-col" @change="onRowDataItemChange">
                    <option value="">{{ gsColumnHeaders.length ? '— column —' : 'column name' }}</option>
                    <option v-for="h in gsAvailableCols(rowDataItems, i)" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <input v-model="item.val" class="ivr-input row-builder-val" placeholder="value or {{variable}}" @input="onRowDataItemChange" />
                  <button class="btn-row-remove" @click="rowDataItems.splice(i,1);onRowDataItemChange()" title="Remove">×</button>
                </div>
                <button
                  class="btn-row-add"
                  :disabled="gsColumnHeaders.length > 0 && rowDataItems.length >= gsColumnHeaders.length"
                  @click="rowDataItems.push({col:'',val:''});onRowDataItemChange()"
                >+ Add Column</button>
              </div>
            </div>
          </template>

          <!-- ── google_sheets_update ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_sheets_update'">
            <div class="ivr-section-label">Google Sheets — Update Row</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onGsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Spreadsheet</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.spreadsheet_id" class="ivr-select" @change="onGsSpreadsheetChange" :disabled="!selectedNodeData.config.google_connection_id || gsLoadingSpreadsheets">
                  <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                  <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGsSpreadsheets(selectedNodeData.config.google_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Sheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.sheet_name" class="ivr-select" @change="onGsSheetChange" :disabled="!selectedNodeData.config.spreadsheet_id || gsLoadingSheets">
                  <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                  <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.spreadsheet_id" @click="loadGsSheetsInSpread(selectedNodeData.config.google_connection_id, selectedNodeData.config.spreadsheet_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Match Row Where</label>
              <div class="filter-row">
                <select v-model="selectedNodeData.config.filter_column" class="ivr-select" style="flex:1" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.sheet_name">
                  <option value="">{{ gsLoadingHeaders ? 'Loading...' : (gsColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                  <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
                <span style="padding:0 6px;opacity:.6;font-size:13px">==</span>
                <input v-model="selectedNodeData.config.filter_value" class="ivr-input" placeholder="{{variable}} or value" @input="onNodeConfigChange" />
              </div>
            </div>
            <div class="ivr-field-group">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                <label style="margin:0">Set Columns</label>
                <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNodeData.config.sheet_name" @click="loadGsColumnHeaders(selectedNodeData.config.google_connection_id, selectedNodeData.config.spreadsheet_id, selectedNodeData.config.sheet_name)" title="Refresh columns">↻</button>
              </div>
              <div class="row-builder">
                <div v-for="(item, i) in setDataItems" :key="i" class="row-builder-item">
                  <select v-model="item.col" class="ivr-select row-builder-col" @change="onSetDataItemChange">
                    <option value="">{{ gsColumnHeaders.length ? '— column —' : 'column name' }}</option>
                    <option v-for="h in gsAvailableCols(setDataItems, i)" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <input v-model="item.val" class="ivr-input row-builder-val" placeholder="new value or {{variable}}" @input="onSetDataItemChange" />
                  <button class="btn-row-remove" @click="setDataItems.splice(i,1);onSetDataItemChange()" title="Remove">×</button>
                </div>
                <button
                  class="btn-row-add"
                  :disabled="gsColumnHeaders.length > 0 && setDataItems.length >= gsColumnHeaders.length"
                  @click="setDataItems.push({col:'',val:''});onSetDataItemChange()"
                >+ Add Column</button>
              </div>
            </div>
          </template>

          <!-- ── google_drive_upload ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_drive_upload'">
            <div class="ivr-section-label">Google Drive — Upload File</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onDriveConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>File URL</label>
              <input v-model="selectedNodeData.config.file_url" class="ivr-input" placeholder="https://... or {{file_url}}" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Public URL. The file will be fetched and uploaded to Drive.</small>
            </div>
            <div class="ivr-field-group">
              <label>File Name <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.file_name" class="ivr-input" placeholder="Auto-detected from URL" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Folder <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.folder_id" class="ivr-select" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.google_connection_id || gdLoadingFolders">
                  <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                  <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGdFolders(selectedNodeData.config.google_connection_id)" title="Refresh folders">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Save result as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="uploaded_file_id" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Saves &#123;&#123;result&#125;&#125; (file ID) and &#123;&#123;result_url&#125;&#125; (share link).</small>
            </div>
          </template>

          <!-- ── google_drive_create_folder ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_drive_create_folder'">
            <div class="ivr-section-label">Google Drive — Create Folder</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onDriveConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Folder Name</label>
              <input v-model="selectedNodeData.config.folder_name" class="ivr-input" placeholder="{{customer_name}} Documents" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Parent Folder <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.parent_folder_id" class="ivr-select" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.google_connection_id || gdLoadingFolders">
                  <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                  <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGdFolders(selectedNodeData.config.google_connection_id)" title="Refresh folders">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Save folder ID as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="created_folder_id" @input="onNodeConfigChange" />
            </div>
          </template>

          <!-- ── google_docs_create ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_docs_create'">
            <div class="ivr-section-label">Google Docs — Create Document</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onDriveConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Document Title</label>
              <input v-model="selectedNodeData.config.doc_title" class="ivr-input" placeholder="{{customer_name}} — Report" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Initial Content <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <textarea v-model="selectedNodeData.config.content" class="ivr-textarea" rows="4" placeholder="Dear {{customer_name}}, Here is your report..." @input="onNodeConfigChange"></textarea>
            </div>
            <div class="ivr-field-group">
              <label>Folder <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.folder_id" class="ivr-select" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.google_connection_id || gdLoadingFolders">
                  <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                  <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.google_connection_id" @click="loadGdFolders(selectedNodeData.config.google_connection_id)" title="Refresh folders">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Save doc ID as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="created_doc_id" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Use &#123;&#123;created_doc_id&#125;&#125; in an Append Text node to add more content.</small>
            </div>
          </template>

          <!-- ── google_docs_append ── -->
          <template v-else-if="selectedNodeData.ivrType === 'google_docs_append'">
            <div class="ivr-section-label">Google Docs — Append Text</div>
            <div class="ivr-field-group">
              <label>Google Account</label>
              <select v-model="selectedNodeData.config.google_connection_id" class="ivr-select" @change="onNodeConfigChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Document ID</label>
              <input v-model="selectedNodeData.config.doc_id" class="ivr-input" placeholder="{{created_doc_id}} or paste ID" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Found in the Docs URL: /document/d/<strong>[ID]</strong>/edit</small>
            </div>
            <div class="ivr-field-group">
              <label>Text to Append</label>
              <textarea v-model="selectedNodeData.config.content" class="ivr-textarea" rows="4" placeholder="New section: {{customer_name}} — {{appointment_date}}" @input="onNodeConfigChange"></textarea>
            </div>
          </template>

          <!-- ═══════════════ Microsoft ═══════════════ -->

          <!-- Microsoft: Not connected -->
          <template v-else-if="isMicrosoftNodeType(selectedNodeData.ivrType) && microsoftConnections.length === 0">
            <div class="ivr-section-label">Microsoft Integration</div>
            <div style="text-align:center;padding:20px 12px">
              <p style="font-size:13px;opacity:.7;margin-bottom:12px">No Microsoft account connected. Connect one first.</p>
              <router-link to="/settings/microsoft" target="_blank" class="ivr-btn-add">Open Microsoft Integrations</router-link>
            </div>
          </template>

          <!-- ── excel_read ── -->
          <template v-else-if="selectedNodeData.ivrType === 'excel_read'">
            <div class="ivr-section-label">Excel Online — Read Rows</div>
            <div class="ivr-field-group">
              <label>Microsoft Account</label>
              <select v-model="selectedNodeData.config.microsoft_connection_id" class="ivr-select" @change="onMsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Workbook</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.workbook_id" class="ivr-select" @change="onMsWorkbookChange" :disabled="!selectedNodeData.config.microsoft_connection_id || msLoadingWorkbooks">
                  <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                  <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.microsoft_connection_id" @click="loadMsWorkbooks(selectedNodeData.config.microsoft_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Worksheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.worksheet_name" class="ivr-select" @change="onMsWorksheetChange" :disabled="!selectedNodeData.config.workbook_id || msLoadingWorksheets">
                  <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                  <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.workbook_id" @click="loadMsWorksheetsInBook(selectedNodeData.config.microsoft_connection_id, selectedNodeData.config.workbook_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Filter</label>
              <div style="display:flex;gap:6px">
                <select v-model="selectedNodeData.config.filter_column" class="ivr-select" style="flex:1" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.worksheet_name || msLoadingHeaders">
                  <option value="">{{ msLoadingHeaders ? 'Loading...' : (msColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                  <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
                <select v-model="selectedNodeData.config.filter_operator" class="ivr-select" style="width:110px" @change="onNodeConfigChange">
                  <option value="==">equals</option>
                  <option value="contains">contains</option>
                  <option value="starts_with">starts with</option>
                  <option value="!=">not equals</option>
                </select>
              </div>
              <input v-if="selectedNodeData.config.filter_column" v-model="selectedNodeData.config.filter_value" class="ivr-input" placeholder="value or {{variable}}" @input="onNodeConfigChange" style="margin-top:6px" />
            </div>
            <div class="ivr-field-group">
              <label>Limit rows</label>
              <input type="number" v-model.number="selectedNodeData.config.limit" class="ivr-input" min="1" max="1000" @input="onNodeConfigChange" style="width:80px" />
            </div>
            <div class="ivr-field-group">
              <label>Save result as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="excel_rows" @input="onNodeConfigChange" />
            </div>
            <p class="ivr-config-info">Routes: <strong>found</strong> (1+ rows match) → <strong>not_found</strong> (no rows match).</p>
          </template>

          <!-- ── excel_append ── -->
          <template v-else-if="selectedNodeData.ivrType === 'excel_append'">
            <div class="ivr-section-label">Excel Online — Add Row</div>
            <div class="ivr-field-group">
              <label>Microsoft Account</label>
              <select v-model="selectedNodeData.config.microsoft_connection_id" class="ivr-select" @change="onMsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Workbook</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.workbook_id" class="ivr-select" @change="onMsWorkbookChange" :disabled="!selectedNodeData.config.microsoft_connection_id || msLoadingWorkbooks">
                  <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                  <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.microsoft_connection_id" @click="loadMsWorkbooks(selectedNodeData.config.microsoft_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Worksheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.worksheet_name" class="ivr-select" @change="onMsWorksheetChange" :disabled="!selectedNodeData.config.workbook_id || msLoadingWorksheets">
                  <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                  <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.workbook_id" @click="loadMsWorksheetsInBook(selectedNodeData.config.microsoft_connection_id, selectedNodeData.config.workbook_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                <label style="margin:0">Row Data</label>
                <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNodeData.config.worksheet_name" @click="loadMsColumnHeaders(selectedNodeData.config.microsoft_connection_id, selectedNodeData.config.workbook_id, selectedNodeData.config.worksheet_name)" title="Refresh columns">↻</button>
              </div>
              <div v-for="(item, idx) in msRowDataItems" :key="idx" style="display:flex;gap:6px;margin-bottom:6px;align-items:center">
                <select v-model="item.col" class="ivr-select" style="flex:1" @change="syncMsRowData('append')">
                  <option value="">{{ msColumnHeaders.length ? '— column —' : 'column name' }}</option>
                  <option v-for="h in msAvailableCols(msRowDataItems, idx)" :key="h" :value="h">{{ h }}</option>
                </select>
                <input v-model="item.val" class="ivr-input" placeholder="value or {{variable}}" style="flex:2" @input="syncMsRowData('append')" />
                <button class="btn-row-remove" @click="msRowDataItems.splice(idx,1); syncMsRowData('append')">✕</button>
              </div>
              <button
                class="btn-row-add"
                :disabled="msColumnHeaders.length > 0 && msRowDataItems.length >= msColumnHeaders.length"
                @click="msRowDataItems.push({col:'',val:''}); syncMsRowData('append')"
              >+ Add Column</button>
            </div>
          </template>

          <!-- ── excel_update ── -->
          <template v-else-if="selectedNodeData.ivrType === 'excel_update'">
            <div class="ivr-section-label">Excel Online — Update Row</div>
            <div class="ivr-field-group">
              <label>Microsoft Account</label>
              <select v-model="selectedNodeData.config.microsoft_connection_id" class="ivr-select" @change="onMsConnectionChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Workbook</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.workbook_id" class="ivr-select" @change="onMsWorkbookChange" :disabled="!selectedNodeData.config.microsoft_connection_id || msLoadingWorkbooks">
                  <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                  <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.microsoft_connection_id" @click="loadMsWorkbooks(selectedNodeData.config.microsoft_connection_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Worksheet Tab</label>
              <div class="gs-picker-row">
                <select v-model="selectedNodeData.config.worksheet_name" class="ivr-select" @change="onMsWorksheetChange" :disabled="!selectedNodeData.config.workbook_id || msLoadingWorksheets">
                  <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                  <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                </select>
                <button class="btn-gs-refresh" :disabled="!selectedNodeData.config.workbook_id" @click="loadMsWorksheetsInBook(selectedNodeData.config.microsoft_connection_id, selectedNodeData.config.workbook_id)" title="Refresh">↻</button>
              </div>
            </div>
            <div class="ivr-field-group">
              <label>Match Row Where</label>
              <div style="display:flex;gap:6px;align-items:center">
                <select v-model="selectedNodeData.config.filter_column" class="ivr-select" style="flex:1" @change="onNodeConfigChange" :disabled="!selectedNodeData.config.worksheet_name || msLoadingHeaders">
                  <option value="">{{ msLoadingHeaders ? 'Loading...' : (msColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                  <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
                <span style="opacity:.6;font-size:12px">==</span>
                <input v-model="selectedNodeData.config.filter_value" class="ivr-input" placeholder="{{variable}} or value" @input="onNodeConfigChange" style="flex:2" />
              </div>
            </div>
            <div class="ivr-field-group">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                <label style="margin:0">Set Columns</label>
                <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNodeData.config.worksheet_name" @click="loadMsColumnHeaders(selectedNodeData.config.microsoft_connection_id, selectedNodeData.config.workbook_id, selectedNodeData.config.worksheet_name)" title="Refresh columns">↻</button>
              </div>
              <div v-for="(item, idx) in msSetDataItems" :key="idx" style="display:flex;gap:6px;margin-bottom:6px;align-items:center">
                <select v-model="item.col" class="ivr-select" style="flex:1" @change="syncMsRowData('update')">
                  <option value="">{{ msColumnHeaders.length ? '— column —' : 'column name' }}</option>
                  <option v-for="h in msAvailableCols(msSetDataItems, idx)" :key="h" :value="h">{{ h }}</option>
                </select>
                <input v-model="item.val" class="ivr-input" placeholder="new value or {{variable}}" style="flex:2" @input="syncMsRowData('update')" />
                <button class="btn-row-remove" @click="msSetDataItems.splice(idx,1); syncMsRowData('update')">✕</button>
              </div>
              <button
                class="btn-row-add"
                :disabled="msColumnHeaders.length > 0 && msSetDataItems.length >= msColumnHeaders.length"
                @click="msSetDataItems.push({col:'',val:''}); syncMsRowData('update')"
              >+ Add Column</button>
            </div>
          </template>

          <!-- ── onedrive_upload ── -->
          <template v-else-if="selectedNodeData.ivrType === 'onedrive_upload'">
            <div class="ivr-section-label">OneDrive — Upload File</div>
            <div class="ivr-field-group">
              <label>Microsoft Account</label>
              <select v-model="selectedNodeData.config.microsoft_connection_id" class="ivr-select" @change="onNodeConfigChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>File URL</label>
              <input v-model="selectedNodeData.config.file_url" class="ivr-input" placeholder="https://... or {{file_url}}" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>File Name <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.file_name" class="ivr-input" placeholder="e.g. {{customer_name}}_report.pdf" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Folder ID <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.folder_id" class="ivr-input" placeholder="OneDrive folder ID or {{folder_id}}" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Leave blank to upload to OneDrive root.</small>
            </div>
            <div class="ivr-field-group">
              <label>Save result as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="uploaded_file_id" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Saves &#123;&#123;result&#125;&#125; (file ID) and &#123;&#123;result_url&#125;&#125; (share link).</small>
            </div>
          </template>

          <!-- ── onedrive_create_folder ── -->
          <template v-else-if="selectedNodeData.ivrType === 'onedrive_create_folder'">
            <div class="ivr-section-label">OneDrive — Create Folder</div>
            <div class="ivr-field-group">
              <label>Microsoft Account</label>
              <select v-model="selectedNodeData.config.microsoft_connection_id" class="ivr-select" @change="onNodeConfigChange">
                <option value="">— select connected account —</option>
                <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
              </select>
            </div>
            <div class="ivr-field-group">
              <label>Folder Name</label>
              <input v-model="selectedNodeData.config.folder_name" class="ivr-input" placeholder="Reports — {{month}}" @input="onNodeConfigChange" />
            </div>
            <div class="ivr-field-group">
              <label>Parent Folder ID <span style="opacity:.7;font-size:11px">(optional)</span></label>
              <input v-model="selectedNodeData.config.parent_folder_id" class="ivr-input" placeholder="OneDrive folder ID or {{parent_folder_id}}" @input="onNodeConfigChange" />
              <small style="font-size:11px;opacity:.7">Leave blank to create in OneDrive root.</small>
            </div>
            <div class="ivr-field-group">
              <label>Save result as variable</label>
              <input v-model="selectedNodeData.config.result_variable" class="ivr-input" placeholder="new_folder_id" @input="onNodeConfigChange" />
            </div>
          </template>

        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <transition name="ivr-modal-fade">
      <div v-if="deleteConfirm.visible" class="ivr-modal-backdrop" @click.self="deleteConfirm.visible = false">
        <div class="ivr-modal">
          <div class="ivr-modal-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          </div>
          <div class="ivr-modal-title">Delete Node</div>
          <div class="ivr-modal-body">
            Delete <strong>{{ deleteConfirm.nodeName }}</strong>?<br>
            All connections to and from this node will also be removed.
          </div>
          <div class="ivr-modal-actions">
            <button class="ivr-modal-btn ivr-modal-btn-cancel" @click="deleteConfirm.visible = false">Cancel</button>
            <button class="ivr-modal-btn ivr-modal-btn-danger" @click="confirmDeleteNode">Delete</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { IVRService } from '@/services/ivrService'
import DashboardService from '@/services/dashboard'
import VarTextEditor from '@/components/VarTextEditor.vue'

let saveTimer = null

const NODE_SVG_PATHS = {
  ivr_play: 'M8 5v14l11-7z',
  ivr_menu: 'M3 18h6v-2H3v2zm0-5h12v-2H3v2zm0-7v2h18V6H3z',
  ivr_collect_input: 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z',
  ivr_get_speech: 'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zm-1 19.93A8.002 8.002 0 0 1 4 13H2a10 10 0 0 0 9 9.93V23h2v-1.07A10 10 0 0 0 22 13h-2a8.002 8.002 0 0 1-7 6.93z',
  ivr_record_voicemail: 'M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-4h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z',
  ivr_wait: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  ivr_transfer: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  ivr_queue: 'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z',
  ivr_end: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z',
  ivr_condition: 'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3l5 5-5 5V6zm-1 10l-5-5 5-5v10z',
  ivr_set_variable: 'M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z',
  ivr_goto: 'M21 11l-6-6v5H8c-2.76 0-5 2.24-5 5v4h2v-4c0-1.65 1.35-3 3-3h7v5l6-6z',
  ivr_business_hours: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  ivr_blacklist_check: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5zm-2 9l-2-2-1.41 1.41L10 16.83l6.59-6.59L15.17 9l-5.17 5z',
  ivr_lookup_customer: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  ivr_assign_tag: 'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z',
  ivr_create_note: 'M19 3H4.99C3.89 3 3 3.9 3 5l.01 14c0 1.1.89 2 1.99 2H19c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S14.93 13 13 13s-3.5-1.57-3.5-3.5S11.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z',
  ivr_send_whatsapp: 'M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z',
  ivr_webhook: 'M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7',
  ivr_ai_response: 'M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zm-2 10H6V7h12v12zm-9-6c-.83 0-1.5-.67-1.5-1.5S8.17 10 9 10s1.5.67 1.5 1.5S9.83 13 9 13zm6 0c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13zm-6 2h6v2H9v-2z',
  api_call: 'M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z',
  // Ported from WhatsApp Flow Builder
  condition: 'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3l5 5-5 5V6zm-1 10l-5-5 5-5v10z',
  calculator: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z',
  delay: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  current_datetime: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  check_24h_window: 'M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm3 18H5V8h14v11z',
  check_working_hours: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
  check_tag: 'M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16zM7 13.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  qr_generator: 'M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm8 6h2v-2h-2v2zm2-6h-2v2h2v-2zm2 0h-2v2h2v2h-2v2h2v-2h2v-2h-2v-2zm0 6h2v-2h-2v2z',
  send_email: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  currency_exchange: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z',
  csat_rating: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  todo_create: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  remove_tag: 'M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16zM19 6.41L20.59 8 15 13.59 13.41 12 19 6.41zM13 8l1.59 1.59L8 16l-1.41-1.41L13 8z',
  // Google
  google_sheets_read: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  google_sheets_append: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  google_sheets_update: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  google_drive_upload: 'M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z',
  google_drive_create_folder: 'M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z',
  google_docs_create: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
  google_docs_append: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
  // Microsoft
  excel_read: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  excel_append: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  excel_update: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z',
  onedrive_upload: 'M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z',
  onedrive_create_folder: 'M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z'
}

export default {
  name: 'IVRFlowBuilderView',

  components: { VarTextEditor },

  data () {
    return {
      loading: true,
      flowId: null,
      flowName: '',
      flowStatus: 'draft',
      triggerNumber: '',
      flowJson: { version: 1, start_node: null, nodes: {}, variables: {} },
      flowNodes: [],
      queues: [],
      extensions: [],
      agentExtensions: [],
      selectedNodeId: null,
      selectedEdge: null,
      saveStatus: 'idle',
      autoSaveEnabled: false,
      paletteCollapsed: false,
      paletteSearch: '',
      zoom: 1,
      pan: { x: 40, y: 40 },
      canvasW: 2000,
      canvasH: 1400,
      drawingEdge: null,
      nodeDrag: null,
      isPanning: false,
      panStart: null,
      audioFiles: [],
      deleteConfirm: { visible: false, nodeId: null, nodeName: '' },
      pendingRoutes: [],
      tts: { visible: false, text: '', provider: 'openai', voice: 'alloy', generating: false, error: '' },
      upload: { visible: false, file: null, name: '', uploading: false, error: '', dragging: false },
      previewAudioId: null,
      previewPaused: false,
      previewLoading: false,
      previewDuration: 0,
      previewCurrentTime: 0,
      conditionOperators: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'greater_than', 'less_than', 'is_empty', 'is_not_empty'],
      activeVarEditor: null,
      testingApi: false,
      apiTestResponse: null,
      apiTestError: null,
      apiTestNodeId: null,
      detectedResponseKeys: [],
      apiTestVarsDetected: [],
      testSampleValues: {},
      apiVarSearch: '',
      // --- Ported from WhatsApp Flow Builder ---
      googleConnections: [],
      microsoftConnections: [],
      gsSpreadsheets: [],
      gsSheetsInSpread: [],
      gsColumnHeaders: [],
      gsLoadingSpreadsheets: false,
      gsLoadingSheets: false,
      gsLoadingHeaders: false,
      gdFolders: [],
      gdLoadingFolders: false,
      msWorkbooks: [],
      msWorksheetsInBook: [],
      msColumnHeaders: [],
      msLoadingWorkbooks: false,
      msLoadingWorksheets: false,
      msLoadingHeaders: false,
      rowDataItems: [],
      setDataItems: [],
      msRowDataItems: [],
      msSetDataItems: [],
      availableTags: [],
      approvedTemplates: [],
      activeCalcInput: null,
      focusedIvrInput: null,
      varsBarOpen: true,
      currencyList: [
        'AED', 'ARS', 'AUD', 'BDT', 'BRL', 'CAD', 'CHF', 'CLP', 'CNY', 'COP',
        'CZK', 'DKK', 'EGP', 'EUR', 'GBP', 'HKD', 'HRK', 'HUF', 'IDR', 'ILS',
        'INR', 'IQD', 'JPY', 'KRW', 'KWD', 'LBP', 'MAD', 'MXN', 'MYR', 'NGN',
        'NOK', 'NZD', 'OMR', 'PHP', 'PKR', 'PLN', 'QAR', 'RON', 'RUB', 'SAR',
        'SEK', 'SGD', 'THB', 'TRY', 'TWD', 'UAH', 'USD', 'VND', 'ZAR'
      ],
      nodeGroups: [
        {
          label: 'Telephony',
          types: [
            { type: 'ivr_play', label: 'Play', color: '#3B82F6', description: 'Play audio or TTS' },
            { type: 'ivr_menu', label: 'Menu', color: '#8B5CF6', description: 'Collect DTMF key press' },
            { type: 'ivr_collect_input', label: 'Collect Input', color: '#6366F1', description: 'Collect digit sequence and store in variable' },
            { type: 'ivr_get_speech', label: 'Get Speech', color: '#10B981', description: 'Record and transcribe speech' },
            { type: 'ivr_record_voicemail', label: 'Voicemail', color: '#F59E0B', description: 'Record voicemail' },
            { type: 'ivr_wait', label: 'Wait', color: '#94A3B8', description: 'Silent pause' },
            { type: 'ivr_transfer', label: 'Transfer', color: '#22C55E', description: 'Transfer to extension' },
            { type: 'ivr_queue', label: 'Queue', color: '#0EA5E9', description: 'Route to call queue' },
            { type: 'ivr_end', label: 'End', color: '#EF4444', description: 'Hang up the call' }
          ]
        },
        {
          label: 'Logic',
          types: [
            { type: 'ivr_condition', label: 'Condition', color: '#F97316', description: 'Branch on variable' },
            { type: 'condition', label: 'Multi-Condition', color: '#FB923C', description: 'Multi-rule branch — first match wins, with Else fallback' },
            { type: 'ivr_set_variable', label: 'Set Variable', color: '#A855F7', description: 'Set a flow variable' },
            { type: 'ivr_goto', label: 'Go To', color: '#6366F1', description: 'Jump to any node' },
            { type: 'ivr_business_hours', label: 'Business Hours', color: '#14B8A6', description: 'Branch on business hours' },
            { type: 'ivr_blacklist_check', label: 'Blacklist Check', color: '#F43F5E', description: 'Check if caller is blacklisted' },
            { type: 'calculator', label: 'Calculator', color: '#84CC16', description: 'Evaluate math expressions, store result' },
            { type: 'delay', label: 'Delay', color: '#6B7280', description: 'Pause flow execution for set duration' },
            { type: 'current_datetime', label: 'Current Date/Time', color: '#0EA5E9', description: 'Read current date and time into variables' },
            { type: 'check_24h_window', label: 'Check 24h Window', color: '#8B5CF6', description: 'Branch based on hours since last customer contact' },
            { type: 'check_working_hours', label: 'Check Working Hours', color: '#0D9488', description: 'Branch based on company working hours schedule' },
            { type: 'check_tag', label: 'Check Tag', color: '#D97706', description: 'Branch based on customer tags' }
          ]
        },
        {
          label: 'CRM',
          types: [
            { type: 'ivr_lookup_customer', label: 'Lookup Customer', color: '#059669', description: 'Find customer by phone' },
            { type: 'ivr_assign_tag', label: 'Assign Tag', color: '#EAB308', description: 'Tag a customer' },
            { type: 'remove_tag', label: 'Remove Tag', color: '#DC2626', description: 'Remove a tag from the customer' },
            { type: 'ivr_create_note', label: 'Create Note', color: '#64748B', description: 'Add CRM note' },
            { type: 'todo_create', label: 'Create Todo', color: '#16A34A', description: 'Create a task linked to the customer' }
          ]
        },
        {
          label: 'Actions',
          types: [
            { type: 'send_email', label: 'Send Email', color: '#2563EB', description: 'Send an email via Quali-D SMTP' },
            { type: 'qr_generator', label: 'QR Code', color: '#7C3AED', description: 'Generate a QR code and save URL' },
            { type: 'currency_exchange', label: 'Currency Exchange', color: '#B45309', description: 'Convert amount between currencies via live ECB rates' },
            { type: 'csat_rating', label: 'CSAT Rating', color: '#F59E0B', description: 'Collect DTMF star rating from caller (1–5 or 1–10)' }
          ]
        },
        {
          label: 'Integrations',
          types: [
            { type: 'ivr_send_whatsapp', label: 'Send WhatsApp', color: '#25D366', description: 'Send WhatsApp template message' },
            { type: 'ivr_webhook', label: 'Webhook', color: '#7C3AED', description: 'Call external HTTP endpoint' },
            { type: 'api_call', label: 'API Call', color: '#0891B2', description: 'Full REST API call with headers, body, response mapping' },
            { type: 'ivr_ai_response', label: 'AI Response', color: '#EC4899', description: 'Generate AI text via GPT' }
          ]
        },
        {
          label: 'Google Sheets',
          types: [
            { type: 'google_sheets_read', label: 'Read Rows', color: '#15803D', description: 'Read rows from a Google Sheet' },
            { type: 'google_sheets_append', label: 'Add Row', color: '#166534', description: 'Append a new row to a Google Sheet' },
            { type: 'google_sheets_update', label: 'Update Row', color: '#14532D', description: 'Update rows in a Google Sheet' }
          ]
        },
        {
          label: 'Google Drive',
          types: [
            { type: 'google_drive_upload', label: 'Upload File', color: '#1D4ED8', description: 'Upload a file to Google Drive' },
            { type: 'google_drive_create_folder', label: 'Create Folder', color: '#1E40AF', description: 'Create a folder in Google Drive' }
          ]
        },
        {
          label: 'Google Docs',
          types: [
            { type: 'google_docs_create', label: 'Create Doc', color: '#1E3A8A', description: 'Create a new Google Docs document' },
            { type: 'google_docs_append', label: 'Append Text', color: '#1E3A8A', description: 'Append text to a Google Docs document' }
          ]
        },
        {
          label: 'Excel Online',
          types: [
            { type: 'excel_read', label: 'Read Rows', color: '#15803D', description: 'Read rows from an Excel workbook' },
            { type: 'excel_append', label: 'Add Row', color: '#166534', description: 'Add a new row to an Excel workbook' },
            { type: 'excel_update', label: 'Update Row', color: '#14532D', description: 'Update rows in an Excel workbook' }
          ]
        },
        {
          label: 'OneDrive',
          types: [
            { type: 'onedrive_upload', label: 'Upload File', color: '#0078D4', description: 'Upload a file to OneDrive' },
            { type: 'onedrive_create_folder', label: 'Create Folder', color: '#0063B1', description: 'Create a folder in OneDrive' }
          ]
        }
      ]
    }
  },

  watch: {
    selectedNodeId () {
      this.pendingRoutes = []
      this.focusedIvrInput = null
      this.tts.visible = false
      this.tts.text = ''
      this.tts.error = ''
      this.upload.visible = false
      this.upload.file = null
      this.upload.name = ''
      this.upload.error = ''
      this.upload.dragging = false
    }
  },

  computed: {
    selectedNodeData () {
      if (!this.selectedNodeId) return null
      return this.flowNodes.find(n => n.id === this.selectedNodeId) || null
    },

    filteredNodeGroups () {
      const q = (this.paletteSearch || '').trim().toLowerCase()
      if (!q) return this.nodeGroups
      return this.nodeGroups
        .map(group => ({
          ...group,
          types: group.types.filter(nt =>
            nt.label.toLowerCase().includes(q) ||
            (nt.description || '').toLowerCase().includes(q)
          )
        }))
        .filter(group => group.types.length > 0)
    },

    otherNodes () {
      return this.flowNodes.filter(n => n.id !== this.selectedNodeId)
    },

    isAudioNode () {
      return this.selectedNodeData &&
        ['ivr_play', 'ivr_menu', 'ivr_collect_input', 'ivr_get_speech', 'ivr_record_voicemail']
          .includes(this.selectedNodeData.ivrType)
    },

    selectedEdgeMidpoint () {
      if (!this.selectedEdge) return null
      const parts = this.selectedEdge.split('__')
      if (parts.length < 2) return null
      const fromNodeId = parts[0]
      const handle = parts[1]
      const fromNode = this.flowNodes.find(n => n.id === fromNodeId)
      if (!fromNode) return null
      const targetId = handle === 'next' ? fromNode.next : (fromNode.routes || {})[handle]
      const toNode = this.flowNodes.find(n => n.id === targetId)
      if (!toNode) return null
      const NODE_W = 210
      const NODE_H = 62
      let sx, sy
      if (this.isBranchingNode(fromNode.ivrType) && handle !== 'next') {
        const routeIdx = this.getRouteKeys(fromNode).indexOf(handle)
        sx = fromNode.position.x + NODE_W / 2
        sy = fromNode.position.y + NODE_H + 19 + Math.max(0, routeIdx) * 24
      } else {
        sx = fromNode.position.x + NODE_W / 2
        sy = fromNode.position.y + NODE_H
      }
      const tx = toNode.position.x + NODE_W / 2
      const ty = toNode.position.y
      // Midpoint of bezier at t=0.5 ≈ average of endpoints (close enough for our curves)
      const mx = (sx + tx) / 2
      const my = (sy + ty) / 2
      return {
        x: this.pan.x + mx * this.zoom,
        y: this.pan.y + my * this.zoom
      }
    },

    transformStyle () {
      return {
        transform: `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.zoom})`,
        transformOrigin: '0 0',
        position: 'absolute',
        width: '0',
        height: '0'
      }
    },

    computedEdges () {
      const edges = []
      this.flowNodes.forEach(node => {
        if (node.next) {
          const target = this.flowNodes.find(n => n.id === node.next)
          if (target) {
            edges.push({
              id: `${node.id}__next__${target.id}`,
              path: this.edgePath(node, target, 'next')
            })
          }
        }
        if (node.routes) {
          Object.entries(node.routes).forEach(([key, targetId]) => {
            if (!targetId) return
            const target = this.flowNodes.find(n => n.id === targetId)
            if (target) {
              edges.push({
                id: `${node.id}__${key}__${targetId}`,
                path: this.edgePath(node, target, key)
              })
            }
          })
        }
      })
      return edges
    },

    drawingEdgePath () {
      if (!this.drawingEdge) return ''
      const { fromX, fromY, currentX, currentY } = this.drawingEdge
      const cx1 = fromX + (currentX - fromX) * 0.5
      const cy1 = fromY
      const cx2 = fromX + (currentX - fromX) * 0.5
      const cy2 = currentY
      return `M ${fromX} ${fromY} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${currentX} ${currentY}`
    },

    saveStatusText () {
      return { idle: '', saving: 'Saving...', saved: 'Saved', error: 'Save failed', unsaved: 'Unsaved changes' }[this.saveStatus] || ''
    },

    availableMenuKeys () {
      if (!this.selectedNodeData || this.selectedNodeData.ivrType !== 'ivr_menu') return []
      const all = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#', '_default', '_timeout']
      const usedCommitted = Object.keys(this.selectedNodeData.routes || {})
      const usedPending = (this.pendingRoutes || []).map(r => r.key).filter(Boolean)
      return all.filter(k => !usedCommitted.includes(k) && !usedPending.includes(k))
    },

    extensionIsVariable () {
      const val = this.selectedNodeData?.config?.extension || ''
      return val.includes('{{')
    },

    ivrFlowVariables () {
      // System variables are always available regardless of position in the flow
      const vars = {
        caller_id: {
          description: 'Caller phone number',
          source: 'system'
        },
        did: {
          description: 'Called DID / trunk number',
          source: 'system'
        }
      }
      // Only expose variables produced by upstream (ancestor) nodes when a node is selected.
      // When nothing is selected show every variable in the flow.
      const upstream = this.selectedNodeId ? this.getUpstreamNodeIds(this.selectedNodeId) : null
      const nodes = upstream ? this.flowNodes.filter(n => upstream.has(n.id)) : this.flowNodes
      for (const node of nodes) {
        const c = node.config || {}
        const t = node.ivrType
        const label = node.name || this.getNodeLabel(t)
        // ── Telephony ──────────────────────────────────────────────────────
        if (t === 'ivr_get_speech' && c.save_to) {
          vars[c.save_to] = {
            description: `Captured by "${label}"`,
            source: 'input',
            nodeId: node.id,
            nodeName: label
          }
        }
        if (t === 'ivr_collect_input' && c.variable) {
          vars[c.variable] = {
            description: `Collected by "${label}"`,
            source: 'input',
            nodeId: node.id,
            nodeName: label
          }
        }
        if (t === 'ivr_menu' && c.save_to) {
          vars[c.save_to] = {
            description: `Key pressed in "${label}"`,
            source: 'input',
            nodeId: node.id,
            nodeName: label
          }
        }
        // ── Logic ──────────────────────────────────────────────────────────
        if (t === 'ivr_set_variable' && c.variable) {
          vars[c.variable] = {
            description: `Set by "${label}"`,
            source: 'set',
            nodeId: node.id,
            nodeName: label
          }
        }
        if (t === 'calculator') {
          for (const calc of (c.calculations || [])) {
            if (calc.result_variable) {
              vars[calc.result_variable] = {
                description: `Calculated by "${label}"`,
                source: 'calc',
                nodeId: node.id,
                nodeName: label
              }
            }
          }
        }
        if (t === 'current_datetime' && c.prefix) {
          const p = c.prefix
          for (const s of ['date', 'time', 'datetime', 'timestamp', 'day', 'month', 'year', 'hour', 'minute', 'weekday', 'day_name']) {
            vars[`${p}_${s}`] = {
              description: `Current ${s} from "${label}"`,
              source: 'datetime',
              nodeId: node.id,
              nodeName: label
            }
          }
        }
        // ── CRM ────────────────────────────────────────────────────────────
        if (t === 'ivr_lookup_customer') {
          vars.customer_name = {
            description: 'Customer name (from lookup)',
            source: 'crm',
            nodeId: node.id,
            nodeName: label
          }
          vars.customer_id = {
            description: 'Customer ID (from lookup)',
            source: 'crm',
            nodeId: node.id,
            nodeName: label
          }
          vars.customer_found = {
            description: '"true" or "false" — was caller found?',
            source: 'crm',
            nodeId: node.id,
            nodeName: label
          }
        }
        // ── AI / Integrations ──────────────────────────────────────────────
        if (t === 'ivr_ai_response' && c.result_variable) {
          vars[c.result_variable] = {
            description: `AI text from "${label}"`,
            source: 'ai',
            nodeId: node.id,
            nodeName: label
          }
        }
        if ((t === 'api_call' || t === 'ivr_webhook') && c.response_mapping) {
          for (const v of Object.values(c.response_mapping)) {
            if (v) {
              vars[v] = {
                description: `API response from "${label}"`,
                source: 'api',
                nodeId: node.id,
                nodeName: label
              }
            }
          }
        }
        // ── Data ───────────────────────────────────────────────────────────
        if ((t === 'google_sheets_read' || t === 'excel_read') && c.result_variable) {
          vars[c.result_variable] = {
            description: `Rows from "${label}"`,
            source: 'data',
            nodeId: node.id,
            nodeName: label
          }
          vars[`${c.result_variable}_count`] = {
            description: `Row count from "${label}"`,
            source: 'data',
            nodeId: node.id,
            nodeName: label
          }
        }
        if (['qr_generator', 'currency_exchange', 'csat_rating'].includes(t) && c.result_variable) {
          vars[c.result_variable] = {
            description: `Result from "${label}"`,
            source: 'data',
            nodeId: node.id,
            nodeName: label
          }
        }
      }
      return vars
    },

    filteredApiVars () {
      const q = (this.apiVarSearch || '').toLowerCase().trim()
      if (!q) return this.ivrFlowVariables
      const terms = q.split(/\s+/).filter(t => t)
      const result = {}
      for (const [name, info] of Object.entries(this.ivrFlowVariables)) {
        const norm = name.toLowerCase().replace(/_/g, ' ')
        const desc = (info.description || '').toLowerCase()
        if (terms.every(t => norm.includes(t) || name.toLowerCase().includes(t) || desc.includes(t))) {
          result[name] = info
        }
      }
      return result
    }
  },

  async mounted () {
    this.flowId = this.$route.params.id
    await this.loadFlow()
    try {
      const res = await IVRService.getQueues()
      this.queues = res.queues || []
    } catch (e) {
      // non-fatal
    }
    try {
      const res = await IVRService.getAudio()
      this.audioFiles = res.files || []
    } catch (e) {
      // non-fatal
    }
    try {
      const [extRes, agentsRes] = await Promise.all([
        IVRService.getExtensions(),
        IVRService.getAgentsExtensions()
      ])
      this.extensions = extRes.extensions || []
      this.agentExtensions = (agentsRes.agents || []).filter(a => a.extension)
    } catch (e) {
      // non-fatal
    }
  },

  beforeUnmount () {
    window.removeEventListener('mousemove', this.onWindowMouseMove)
    window.removeEventListener('mouseup', this.onWindowMouseUp)
    window.removeEventListener('keydown', this.onKeyDown)
  },

  methods: {
    async loadFlow () {
      this.loading = true
      try {
        const res = await IVRService.getFlow(this.flowId)
        const flow = res.flow
        this.flowName = flow.name
        this.flowStatus = flow.status
        this.triggerNumber = flow.trigger_number || ''
        this.flowJson = flow.flow_json || { version: 1, start_node: null, nodes: {}, variables: {} }
        this.flowNodes = this.deserializeNodes(this.flowJson)
      } catch (e) {
        alert('Error loading flow: ' + e.message)
      } finally {
        this.loading = false
      }
      window.addEventListener('mousemove', this.onWindowMouseMove)
      window.addEventListener('mouseup', this.onWindowMouseUp)
      window.addEventListener('keydown', this.onKeyDown)
    },

    deserializeNodes (fj) {
      if (!fj || !fj.nodes) return []
      return Object.values(fj.nodes).map(n => {
        // Merge saved config on top of defaults so every expected key is present
        // and Vue 2 can make them all reactive from the start.
        const defaults = this.getNodeDefaults(n.type)
        return {
          id: n.id,
          ivrType: n.type,
          name: n.name || n.type,
          position: n.position || { x: 100, y: 100 },
          config: { ...defaults.config, ...n.config },
          next: n.next || '',
          routes: n.routes ? { ...n.routes } : {}
        }
      })
    },

    serializeToFlowJson () {
      const nodes = {}
      this.flowNodes.forEach(n => {
        const node = {
          id: n.id,
          type: n.ivrType,
          name: n.name,
          config: { ...n.config },
          position: { ...n.position }
        }
        if (this.isBranchingNode(n.ivrType)) {
          node.routes = { ...n.routes }
        } else {
          node.next = n.next || null
        }
        nodes[n.id] = node
      })
      return {
        version: 1,
        start_node: this.flowJson.start_node,
        nodes,
        variables: this.flowJson.variables || {}
      }
    },

    onNodeConfigChange () {
      this.scheduleSave()
    },

    scheduleSave () {
      if (!this.autoSaveEnabled) {
        if (saveTimer) clearTimeout(saveTimer)
        this.saveStatus = 'unsaved'
        return
      }
      if (saveTimer) clearTimeout(saveTimer)
      this.saveStatus = 'saving'
      saveTimer = setTimeout(() => this.autoSave(), 1200)
    },

    async autoSave () {
      if (saveTimer) clearTimeout(saveTimer)
      this.saveStatus = 'saving'
      try {
        await IVRService.updateFlow(this.flowId, {
          name: this.flowName,
          trigger_number: this.triggerNumber || null,
          flow_json: this.serializeToFlowJson()
        })
        this.saveStatus = 'saved'
        setTimeout(() => { if (this.saveStatus === 'saved') this.saveStatus = 'idle' }, 2000)
      } catch (e) {
        this.saveStatus = 'error'
      }
    },

    async toggleFlowStatus () {
      try {
        if (this.flowStatus === 'active') {
          await IVRService.deactivateFlow(this.flowId)
          this.flowStatus = 'inactive'
        } else {
          await IVRService.activateFlow(this.flowId)
          this.flowStatus = 'active'
        }
      } catch (e) {
        alert('Error: ' + e.message)
      }
    },

    setAsStart (nodeId) {
      this.flowJson.start_node = nodeId
      this.scheduleSave()
    },

    promptDeleteNode (node) {
      this.deleteConfirm = { visible: true, nodeId: node.id, nodeName: node.name || this.getNodeLabel(node.ivrType) }
    },

    confirmDeleteNode () {
      this.deleteNode(this.deleteConfirm.nodeId)
      this.deleteConfirm.visible = false
    },

    deleteNode (nodeId) {
      this.flowNodes = this.flowNodes.filter(n => n.id !== nodeId)
      this.flowNodes.forEach(n => {
        if (n.next === nodeId) n.next = null
        if (n.routes) {
          Object.keys(n.routes).forEach(k => {
            if (n.routes[k] === nodeId) n.routes[k] = ''
          })
        }
      })
      if (this.flowJson.start_node === nodeId) this.flowJson.start_node = null
      this.selectedNodeId = null
      this.scheduleSave()
    },

    selectNode (node) {
      this.selectedNodeId = node.id
      this.selectedEdge = null
      this._onNodeSelected(node)
    },

    _onNodeSelected (node) {
      const t = node.ivrType
      const c = node.config || {}
      // Tags
      if (['ivr_assign_tag', 'remove_tag', 'check_tag'].includes(t) && !this.availableTags.length) {
        this.loadAvailableTags()
      }
      // WhatsApp templates
      if (t === 'ivr_send_whatsapp' && !this.approvedTemplates.length) {
        this.loadApprovedTemplates()
      }
      // Support both string (raw API) and already-parsed object (pre-parsed by HTTP layer)
      const parseRowJson = (raw) => {
        if (!raw) return {}
        if (typeof raw === 'object') return raw
        try { return JSON.parse(raw) } catch { return {} }
      }
      // Google nodes
      if (this.isGoogleNodeType(t)) {
        if (!this.googleConnections.length) this.loadGoogleConnections()
        if (c.google_connection_id) {
          if (!this.gsSpreadsheets.length && c.spreadsheet_id) this.loadGsSpreadsheets(c.google_connection_id)
          if (!this.gsSheetsInSpread.length && c.sheet_name) this.loadGsSheetsInSpread(c.google_connection_id, c.spreadsheet_id)
          if (!this.gdFolders.length && (t === 'google_drive_upload' || t === 'google_drive_create_folder' || t === 'google_docs_create')) this.loadGdFolders(c.google_connection_id)
          if ((t === 'google_sheets_append' || t === 'google_sheets_update' || t === 'google_sheets_read') && c.spreadsheet_id && c.sheet_name) {
            this.loadGsColumnHeaders(c.google_connection_id, c.spreadsheet_id, c.sheet_name)
          }
        }
        // Restore rowDataItems / setDataItems from JSON
        if (t === 'google_sheets_append') {
          const obj = parseRowJson(c.row_data_json)
          this.rowDataItems = Object.entries(obj).map(([col, val]) => ({ col, val }))
          if (!this.rowDataItems.length) this.rowDataItems = [{ col: '', val: '' }]
        }
        if (t === 'google_sheets_update') {
          const obj = parseRowJson(c.set_data_json)
          this.setDataItems = Object.entries(obj).map(([col, val]) => ({ col, val }))
          if (!this.setDataItems.length) this.setDataItems = [{ col: '', val: '' }]
        }
      }
      // Microsoft nodes
      if (this.isMicrosoftNodeType(t)) {
        if (!this.microsoftConnections.length) this.loadMicrosoftConnections()
        if (c.microsoft_connection_id) {
          if (!this.msWorkbooks.length && c.workbook_id) this.loadMsWorkbooks(c.microsoft_connection_id)
          if (!this.msWorksheetsInBook.length && c.worksheet_name) this.loadMsWorksheetsInBook(c.microsoft_connection_id, c.workbook_id)
          if ((t === 'excel_append' || t === 'excel_update' || t === 'excel_read') && c.workbook_id && c.worksheet_name) {
            this.loadMsColumnHeaders(c.microsoft_connection_id, c.workbook_id, c.worksheet_name)
          }
        }
        if (t === 'excel_append') {
          const obj = parseRowJson(c.row_data_json)
          this.msRowDataItems = Object.entries(obj).map(([col, val]) => ({ col, val }))
          if (!this.msRowDataItems.length) this.msRowDataItems = [{ col: '', val: '' }]
        }
        if (t === 'excel_update') {
          const obj = parseRowJson(c.set_data_json)
          this.msSetDataItems = Object.entries(obj).map(([col, val]) => ({ col, val }))
          if (!this.msSetDataItems.length) this.msSetDataItems = [{ col: '', val: '' }]
        }
      }
    },

    selectEdge (id) {
      this.selectedEdge = id
      this.selectedNodeId = null
    },

    onCanvasClick () {
      this.selectedNodeId = null
      this.selectedEdge = null
    },

    onPaletteDragStart (e, nt) {
      e.dataTransfer.setData('nodeType', nt.type)
    },

    onCanvasDragOver (e) {
      e.preventDefault()
    },

    onCanvasDrop (e) {
      const nodeType = e.dataTransfer.getData('nodeType')
      if (!nodeType) return
      const rect = this.$refs.canvasRef.getBoundingClientRect()
      const x = (e.clientX - rect.left - this.pan.x) / this.zoom
      const y = (e.clientY - rect.top - this.pan.y) / this.zoom
      this.addNode(nodeType, { x, y })
    },

    addNode (nodeType, position) {
      const id = 'node_' + Math.random().toString(36).substr(2, 9)
      const defaults = this.getNodeDefaults(nodeType)
      const node = {
        id,
        ivrType: nodeType,
        name: this.getNodeLabel(nodeType),
        position,
        config: { ...defaults.config },
        next: '',
        routes: { ...defaults.routes }
      }
      this.flowNodes.push(node)
      if (!this.flowJson.start_node) this.flowJson.start_node = id
      this.selectedNodeId = id
      this.scheduleSave()
    },

    startNodeDrag (e, node) {
      if (e.button !== 0) return
      this.nodeDrag = {
        nodeId: node.id,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startNodeX: node.position.x,
        startNodeY: node.position.y
      }
    },

    onCanvasMouseDown (e) {
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        this.isPanning = true
        this.panStart = { x: e.clientX - this.pan.x, y: e.clientY - this.pan.y }
      }
    },

    onCanvasMouseMove (e) {
      if (this.isPanning && this.panStart) {
        this.pan.x = e.clientX - this.panStart.x
        this.pan.y = e.clientY - this.panStart.y
      }
    },

    onCanvasMouseUp () {
      this.isPanning = false
      this.panStart = null
    },

    onWindowMouseMove (e) {
      if (this.nodeDrag) {
        const node = this.flowNodes.find(n => n.id === this.nodeDrag.nodeId)
        if (node) {
          node.position.x = this.nodeDrag.startNodeX + (e.clientX - this.nodeDrag.startMouseX) / this.zoom
          node.position.y = this.nodeDrag.startNodeY + (e.clientY - this.nodeDrag.startMouseY) / this.zoom
        }
      }
      if (this.isPanning && this.panStart) {
        this.pan.x = e.clientX - this.panStart.x
        this.pan.y = e.clientY - this.panStart.y
      }
      if (this.drawingEdge) {
        const rect = this.$refs.canvasRef.getBoundingClientRect()
        this.drawingEdge.currentX = e.clientX - rect.left
        this.drawingEdge.currentY = e.clientY - rect.top
      }
    },

    onWindowMouseUp (e) {
      if (this.nodeDrag) {
        this.nodeDrag = null
        this.scheduleSave()
      }
      if (this.drawingEdge) {
        const target = document.elementFromPoint(e.clientX, e.clientY)
        const handleEl = target?.closest('[data-handle="input"]')
        if (handleEl) {
          const targetNodeId = handleEl.dataset.node
          const fromHandle = this.drawingEdge.fromHandle
          // Reject: source must be an output handle (not 'input')
          if (targetNodeId && targetNodeId !== this.drawingEdge.fromNode && fromHandle !== 'input') {
            this.connectNodes(this.drawingEdge.fromNode, fromHandle, targetNodeId)
          }
        }
        this.drawingEdge = null
      }
      this.isPanning = false
      this.panStart = null
    },

    startEdgeDraw (e, nodeId, handle) {
      e.stopPropagation()
      const rect = this.$refs.canvasRef.getBoundingClientRect()
      this.drawingEdge = {
        fromNode: nodeId,
        fromHandle: handle,
        fromX: e.clientX - rect.left,
        fromY: e.clientY - rect.top,
        currentX: e.clientX - rect.left,
        currentY: e.clientY - rect.top
      }
    },

    connectNodes (fromNodeId, handle, toNodeId) {
      const fromNode = this.flowNodes.find(n => n.id === fromNodeId)
      if (!fromNode) return
      if (handle === 'next') {
        fromNode.next = toNodeId
      } else {
        if (!fromNode.routes) fromNode.routes = {}
        fromNode.routes[handle] = toNodeId
      }
      this.scheduleSave()
    },

    onWheel (e) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      this.zoom = Math.min(2, Math.max(0.3, this.zoom + delta))
    },

    edgePath (fromNode, toNode, handle) {
      const NODE_W = 210
      const NODE_H = 62
      const routeKeys = this.getRouteKeys(fromNode)
      const routeIdx = routeKeys.indexOf(handle)

      // All coordinates are in local (pre-transform) space.
      // The SVG <g> applies pan+zoom, so we must NOT include pan/zoom here.
      let sx, sy
      if (this.isBranchingNode(fromNode.ivrType) && routeIdx >= 0) {
        // Route handle: centered at bottom of node, stacked vertically
        sx = fromNode.position.x + NODE_W / 2
        sy = fromNode.position.y + NODE_H + 19 + routeIdx * 24
      } else {
        // Regular output handle: center is at exactly node bottom (bottom:-7px handle center)
        sx = fromNode.position.x + NODE_W / 2
        sy = fromNode.position.y + NODE_H
      }

      // Input handle: centered at top of target node (top:-7px → center at -0px from top)
      const tx = toNode.position.x + NODE_W / 2
      const ty = toNode.position.y

      // Adaptive cubic bezier — strength scales with vertical distance
      const dist = Math.abs(ty - sy)
      const strength = Math.min(Math.max(dist * 0.45, 50), 200)

      return `M ${sx} ${sy} C ${sx} ${sy + strength}, ${tx} ${ty - strength}, ${tx} ${ty}`
    },

    isBranchingNode (type) {
      return ['ivr_menu', 'ivr_collect_input', 'ivr_condition', 'ivr_business_hours', 'ivr_blacklist_check', 'ivr_lookup_customer',
        'condition', 'check_24h_window', 'check_working_hours', 'check_tag',
        'google_sheets_read', 'excel_read'].includes(type)
    },

    getRouteKeys (node) {
      if (node.ivrType === 'ivr_condition') return ['true', 'false']
      if (node.ivrType === 'ivr_business_hours') return ['open', 'closed']
      if (node.ivrType === 'ivr_blacklist_check') return ['blocked', 'allowed']
      if (node.ivrType === 'ivr_lookup_customer') return ['found', 'not_found']
      if (node.ivrType === 'check_24h_window') return ['within', 'outside']
      if (node.ivrType === 'check_working_hours') return ['open', 'closed']
      if (node.ivrType === 'google_sheets_read' || node.ivrType === 'excel_read') return ['found', 'not_found']
      if (node.ivrType === 'condition' || node.ivrType === 'check_tag') {
        const conds = node.config?.conditions || []
        return [...conds.map((_, i) => `if_${i + 1}`), 'else']
      }
      if (node.ivrType === 'ivr_menu') {
        // Show ALL defined keys as output ports (target wired on canvas)
        return Object.keys(node.routes || {})
      }
      if (node.ivrType === 'ivr_collect_input') return ['dtmf', 'timeout']
      return []
    },

    getNodeColor (type) {
      const colors = {
        ivr_play: '#3B82F6',
        ivr_menu: '#8B5CF6',
        ivr_collect_input: '#6366F1',
        ivr_get_speech: '#10B981',
        ivr_record_voicemail: '#F59E0B',
        ivr_wait: '#94A3B8',
        ivr_transfer: '#22C55E',
        ivr_queue: '#0EA5E9',
        ivr_end: '#EF4444',
        ivr_condition: '#F97316',
        ivr_set_variable: '#A855F7',
        ivr_goto: '#6366F1',
        ivr_business_hours: '#14B8A6',
        ivr_blacklist_check: '#F43F5E',
        ivr_lookup_customer: '#059669',
        ivr_assign_tag: '#EAB308',
        ivr_create_note: '#64748B',
        ivr_send_whatsapp: '#25D366',
        ivr_webhook: '#7C3AED',
        api_call: '#0891B2',
        ivr_ai_response: '#EC4899',
        // Ported from WA flow builder
        condition: '#FB923C',
        calculator: '#84CC16',
        delay: '#6B7280',
        current_datetime: '#0EA5E9',
        check_24h_window: '#8B5CF6',
        check_working_hours: '#0D9488',
        check_tag: '#D97706',
        qr_generator: '#7C3AED',
        send_email: '#2563EB',
        currency_exchange: '#B45309',
        csat_rating: '#F59E0B',
        todo_create: '#16A34A',
        remove_tag: '#DC2626',
        // Google
        google_sheets_read: '#15803D',
        google_sheets_append: '#166534',
        google_sheets_update: '#14532D',
        google_drive_upload: '#1D4ED8',
        google_drive_create_folder: '#1E40AF',
        google_docs_create: '#1E3A8A',
        google_docs_append: '#1E3A8A',
        // Microsoft
        excel_read: '#107C41',
        excel_append: '#0E6832',
        excel_update: '#0B5527',
        onedrive_upload: '#0078D4',
        onedrive_create_folder: '#0063B1'
      }
      return colors[type] || '#64748B'
    },

    getNodeIconPath (type) {
      return NODE_SVG_PATHS[type] || 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z'
    },

    getNodeLabel (type) {
      const labels = {
        ivr_play: 'Play',
        ivr_menu: 'Menu',
        ivr_collect_input: 'Collect Input',
        ivr_get_speech: 'Get Speech',
        ivr_record_voicemail: 'Record Voicemail',
        ivr_wait: 'Wait',
        ivr_transfer: 'Transfer',
        ivr_queue: 'Queue',
        ivr_end: 'End',
        ivr_condition: 'Condition',
        ivr_set_variable: 'Set Variable',
        ivr_goto: 'Go To',
        ivr_business_hours: 'Business Hours',
        ivr_blacklist_check: 'Blacklist Check',
        ivr_lookup_customer: 'Lookup Customer',
        ivr_assign_tag: 'Assign Tag',
        ivr_create_note: 'Create Note',
        ivr_send_whatsapp: 'Send WhatsApp',
        ivr_webhook: 'Webhook',
        api_call: 'API Call',
        ivr_ai_response: 'AI Response',
        condition: 'Multi-Condition',
        calculator: 'Calculator',
        delay: 'Delay',
        current_datetime: 'Current Date/Time',
        check_24h_window: 'Check 24h Window',
        check_working_hours: 'Check Working Hours',
        check_tag: 'Check Tag',
        qr_generator: 'QR Code',
        send_email: 'Send Email',
        currency_exchange: 'Currency Exchange',
        csat_rating: 'CSAT Rating',
        todo_create: 'Create Todo',
        remove_tag: 'Remove Tag',
        google_sheets_read: 'Read Rows (Sheets)',
        google_sheets_append: 'Add Row (Sheets)',
        google_sheets_update: 'Update Row (Sheets)',
        google_drive_upload: 'Upload File (Drive)',
        google_drive_create_folder: 'Create Folder (Drive)',
        google_docs_create: 'Create Doc',
        google_docs_append: 'Append Text (Docs)',
        excel_read: 'Read Rows (Excel)',
        excel_append: 'Add Row (Excel)',
        excel_update: 'Update Row (Excel)',
        onedrive_upload: 'Upload File (OneDrive)',
        onedrive_create_folder: 'Create Folder (OneDrive)'
      }
      return labels[type] || type
    },

    getNodeSummary (node) {
      const c = node.config || {}
      if (['ivr_play', 'ivr_menu', 'ivr_get_speech', 'ivr_record_voicemail'].includes(node.ivrType)) return c.audio_file_name || ''
      if (node.ivrType === 'ivr_collect_input') return c.variable ? `→ {{${c.variable}}}` : c.audio_file_name || ''
      if (node.ivrType === 'ivr_transfer') return c.extension || ''
      if (node.ivrType === 'ivr_queue') return c.queue_name || ''
      if (node.ivrType === 'ivr_wait') return c.duration ? `${c.duration}s` : ''
      if (node.ivrType === 'ivr_condition') return `${c.left || ''} ${c.operator || ''} ${c.right || ''}`
      if (node.ivrType === 'ivr_set_variable') return `${c.variable || ''} = ${c.value || ''}`
      if (node.ivrType === 'ivr_ai_response') return c.prompt?.slice(0, 45) || ''
      if (node.ivrType === 'api_call') return c.api_node_name ? `${c.http_method || 'POST'} · ${c.api_node_name}` : (c.api_url?.slice(0, 40) || '')
      if (node.ivrType === 'delay') return c.delay_value ? `${c.delay_value} ${c.delay_unit || 'seconds'}` : ''
      if (node.ivrType === 'current_datetime') return c.variable_prefix ? `{{${c.variable_prefix}_date}}` : ''
      if (node.ivrType === 'calculator') return (c.calculations || []).map(r => r.result_variable).filter(Boolean).join(', ')
      if (node.ivrType === 'send_email') return c.to ? `→ ${c.to.slice(0, 30)}` : ''
      if (node.ivrType === 'currency_exchange') return c.result_variable ? `${c.from_currency}→${c.to_currency} → {{${c.result_variable}}}` : ''
      if (node.ivrType === 'todo_create') return c.title?.slice(0, 40) || ''
      if (node.ivrType === 'ivr_send_whatsapp') return c.template_name || ''
      if (['google_sheets_read', 'google_sheets_append', 'google_sheets_update', 'excel_read', 'excel_append', 'excel_update'].includes(node.ivrType)) return c.sheet_name || c.worksheet_name || ''
      if (['google_docs_create', 'google_docs_append'].includes(node.ivrType)) return c.doc_title || ''
      return ''
    },

    getNodeDefaults (type) {
      const configs = {
        ivr_play: { config: { audio_file_id: '', audio_file_name: '' }, routes: {} },
        ivr_menu: { config: { audio_file_id: '', audio_file_name: '', timeout: 5, retries: 3 }, routes: {} },
        ivr_collect_input: { config: { audio_file_id: '', audio_file_name: '', variable: '', num_digits: 11, timeout: 10, retries: 2, terminate_on: '#' }, routes: { dtmf: null, timeout: null } },
        ivr_get_speech: { config: { audio_file_id: '', audio_file_name: '', silence_sec: 3, max_sec: 30 }, routes: {} },
        ivr_record_voicemail: { config: { audio_file_id: '', audio_file_name: '', silence_sec: 5, max_sec: 120 }, routes: {} },
        ivr_wait: { config: { duration: 3 }, routes: {} },
        ivr_transfer: { config: { extension: '', context: 'default', timeout: 30 }, routes: {} },
        ivr_queue: { config: { queue_name: '' }, routes: {} },
        ivr_end: { config: {}, routes: {} },
        ivr_condition: { config: { left: '', operator: 'equals', right: '' }, routes: { true: '', false: '' } },
        ivr_set_variable: { config: { variable: '', value: '' }, routes: {} },
        ivr_goto: { config: { target_node: '' }, routes: {} },
        ivr_business_hours: { config: {}, routes: { open: '', closed: '' } },
        ivr_blacklist_check: { config: {}, routes: { blocked: '', allowed: '' } },
        ivr_lookup_customer: { config: {}, routes: { found: '', not_found: '' } },
        ivr_assign_tag: { config: { tag_id: null }, routes: {} },
        ivr_create_note: { config: { text: '' }, routes: {} },
        ivr_send_whatsapp: { config: { template_name: '', template_language: '', template_components: [], header_type: '', header_params: [], header_media_url: '', body_params: [], button_params: [], override_to: '' }, routes: {} },
        ivr_webhook: { config: { url: '', method: 'POST', fields: {}, response_map: {} }, routes: {} },
        api_call: { config: { api_node_name: '', api_url: '', http_method: 'POST', headers: {}, query_params: {}, body_format: 'json', request_body_json: '', form_params: {}, response_mapping: {}, idmsg_mode: 'off', idmsg_custom_value: '', skip_ssl_verify: false, timeout_seconds: 30 }, routes: {} },
        ivr_ai_response: { config: { prompt: '', save_to: 'ai_reply' }, routes: {} },
        // --- Ported from WhatsApp Flow Builder ---
        condition: { config: { conditions: [{ id: 'cond_1', variable: '', operator: '==', value: '' }] }, routes: { if_1: '', else: '' } },
        calculator: { config: { calculations: [{ operand_a: '', operator: '+', operand_b: '', result_variable: '' }] }, routes: {} },
        delay: { config: { delay_value: 3, delay_unit: 'seconds' }, routes: {} },
        current_datetime: { config: { variable_prefix: 'now', timezone: 'UTC', date_format: 'd/m/Y', time_format: 'H:i', offset_days: 0, skip_weekends: false, weekend_days: [0, 6] }, routes: {} },
        check_24h_window: { config: { hours_limit: 24, result_variable: '' }, routes: { within: '', outside: '' } },
        check_working_hours: { config: { timezone: 'UTC', result_variable: '' }, routes: { open: '', closed: '' } },
        check_tag: { config: { conditions: [{ id: 'cond_1', tag_id: '' }] }, routes: { if_1: '', else: '' } },
        qr_generator: { config: { qr_data: '', size: 300, send_to_customer: false, caption: '', result_variable: 'qr_code_url' }, routes: {} },
        send_email: { config: { to: '', subject: '', body: '', from_name: '', reply_to: '', html_mode: false }, routes: {} },
        currency_exchange: { config: { amount: '', from_currency: 'USD', to_currency: 'EGP', decimal_places: 2, result_variable: 'converted_amount' }, routes: {} },
        csat_rating: { config: { question: '', scale: 5, variable_name: 'csat_rating' }, routes: {} },
        todo_create: { config: { title: '', description: '', priority: 'medium', agent_mode: 'specific', agent_id_static: '' }, routes: {} },
        remove_tag: { config: { tag_id: null }, routes: {} },
        google_sheets_read: { config: { google_connection_id: '', spreadsheet_id: '', sheet_name: '', filter_column: '', filter_operator: '==', filter_value: '', limit: 10, result_variable: 'sheet_rows', sheet_columns: [] }, routes: { found: '', not_found: '' } },
        google_sheets_append: { config: { google_connection_id: '', spreadsheet_id: '', sheet_name: '', row_data_json: '' }, routes: {} },
        google_sheets_update: { config: { google_connection_id: '', spreadsheet_id: '', sheet_name: '', filter_column: '', filter_operator: '==', filter_value: '', set_data_json: '' }, routes: {} },
        google_drive_upload: { config: { google_connection_id: '', folder_id: '', file_url: '', file_name: '', result_variable: 'uploaded_file_id' }, routes: {} },
        google_drive_create_folder: { config: { google_connection_id: '', parent_folder_id: '', folder_name: '', result_variable: 'created_folder_id' }, routes: {} },
        google_docs_create: { config: { google_connection_id: '', doc_title: '', content: '', folder_id: '', result_variable: 'created_doc_id' }, routes: {} },
        google_docs_append: { config: { google_connection_id: '', doc_id: '', content: '' }, routes: {} },
        excel_read: { config: { microsoft_connection_id: '', workbook_id: '', worksheet_name: '', filter_column: '', filter_operator: '==', filter_value: '', limit: 10, result_variable: 'excel_rows', sheet_columns: [] }, routes: { found: '', not_found: '' } },
        excel_append: { config: { microsoft_connection_id: '', workbook_id: '', worksheet_name: '', row_data_json: '' }, routes: {} },
        excel_update: { config: { microsoft_connection_id: '', workbook_id: '', worksheet_name: '', filter_column: '', filter_operator: '==', filter_value: '', set_data_json: '' }, routes: {} },
        onedrive_upload: { config: { microsoft_connection_id: '', folder_id: '', file_url: '', file_name: '', result_variable: 'uploaded_file_id' }, routes: {} },
        onedrive_create_folder: { config: { microsoft_connection_id: '', parent_folder_id: '', folder_name: '', result_variable: 'created_folder_id' }, routes: {} }
      }
      return configs[type] || { config: {}, routes: {} }
    },

    onKeyDown (e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        this.autoSave()
        return
      }
      if (e.target.matches('input, textarea, select')) return
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (this.selectedNodeId) {
        this.deleteNode(this.selectedNodeId)
      } else if (this.selectedEdge) {
        this.deleteEdge(this.selectedEdge)
      }
    },

    deleteEdge (edgeId) {
      // edgeId format: `${fromNodeId}__${handle}__${toNodeId}`
      const parts = edgeId.split('__')
      if (parts.length < 2) return
      const fromNodeId = parts[0]
      const handle = parts[1]
      const fromNode = this.flowNodes.find(n => n.id === fromNodeId)
      if (!fromNode) return
      if (handle === 'next') {
        fromNode.next = ''
      } else if (fromNode.routes) {
        fromNode.routes[handle] = ''
      }
      this.selectedEdge = null
      this.scheduleSave()
    },

    removeMenuRoute (key) {
      delete this.selectedNodeData.routes[key]
      this.onNodeConfigChange()
    },

    addPendingRoute () {
      this.pendingRoutes.push({ key: '', target: '' })
    },

    removePendingRoute (idx) {
      this.pendingRoutes.splice(idx, 1)
    },

    tryCommitPending (idx) {
      const row = this.pendingRoutes[idx]
      if (row.key) {
        // Store key with empty target — wiring done on canvas by dragging the output port
        this.selectedNodeData.routes[row.key] = ''
        this.pendingRoutes.splice(idx, 1)
        this.onNodeConfigChange()
      }
    },

    availableKeysForPending (idx) {
      const all = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#', '_default', '_timeout']
      const usedCommitted = Object.keys(this.selectedNodeData.routes || {})
      const usedPending = this.pendingRoutes.filter((_, i) => i !== idx).map(r => r.key).filter(Boolean)
      return all.filter(k => !usedCommitted.includes(k) && !usedPending.includes(k))
    },

    onAudioSelect () {
      const id = this.selectedNodeData.config.audio_file_id
      const file = this.audioFiles.find(f => f.id === id)
      this.selectedNodeData.config.audio_file_name = file ? file.name : ''
      this.onNodeConfigChange()
    },

    async previewAudio (id) {
      if (!id) return
      // If same file: resume if paused, otherwise stop
      if (this.previewAudioId === id) {
        if (this.previewPaused && this._previewAudioEl) {
          await this._previewAudioEl.play()
          this.previewPaused = false
        }
        return
      }
      // Stop any other file playing
      if (this._previewAudioEl) {
        this._previewAudioEl.pause()
        this._previewAudioEl = null
        this.previewAudioId = null
      }
      this.previewPaused = false
      this.previewLoading = true
      this.previewDuration = 0
      this.previewCurrentTime = 0
      try {
        const blobUrl = await IVRService.getAudioBlobUrl(id)
        const audio = new Audio(blobUrl)
        this._previewAudioEl = audio
        this.previewAudioId = id
        audio.addEventListener('loadedmetadata', () => { this.previewDuration = audio.duration })
        audio.addEventListener('timeupdate', () => { this.previewCurrentTime = audio.currentTime })
        audio.addEventListener('ended', () => {
          if (this._previewAudioEl === audio) {
            this._previewAudioEl = null
            this.previewAudioId = null
            this.previewPaused = false
            this.previewCurrentTime = 0
          }
          URL.revokeObjectURL(blobUrl)
        })
        await audio.play()
      } catch (e) {
        this._previewAudioEl = null
        this.previewAudioId = null
      } finally {
        this.previewLoading = false
      }
    },

    pausePreview () {
      if (this._previewAudioEl) {
        this._previewAudioEl.pause()
        this.previewPaused = true
      }
    },

    resumePreview () {
      if (this._previewAudioEl) {
        this._previewAudioEl.play()
        this.previewPaused = false
      }
    },

    stopPreview () {
      if (this._previewAudioEl) {
        this._previewAudioEl.pause()
        this._previewAudioEl = null
      }
      this.previewAudioId = null
      this.previewPaused = false
      this.previewCurrentTime = 0
    },

    seekPreview (e) {
      if (!this._previewAudioEl || !this.previewDuration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      this._previewAudioEl.currentTime = pct * this.previewDuration
    },

    formatTime (secs) {
      if (!secs || isNaN(secs)) return '0:00'
      const m = Math.floor(secs / 60)
      const s = Math.floor(secs % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    async generateTtsInline () {
      if (!this.tts.text.trim()) return
      if (this.tts.provider === 'elevenlabs' && !this.tts.voice.trim()) return
      this.tts.generating = true
      this.tts.error = ''
      try {
        const res = await IVRService.generateTTS({
          text: this.tts.text.trim(),
          voice: this.tts.voice || 'alloy',
          provider: this.tts.provider,
          name: this.tts.text.trim().slice(0, 40)
        })
        // Refresh the library list so the new file appears in the dropdown
        const listRes = await IVRService.getAudio()
        this.audioFiles = listRes.files || []
        // Auto-select the newly generated file on the node
        const fileId = res.id
        this.selectedNodeData.config.audio_file_id = fileId
        const file = this.audioFiles.find(f => f.id === fileId)
        this.selectedNodeData.config.audio_file_name = file ? file.name : this.tts.text.trim().slice(0, 40)
        this.onNodeConfigChange()
        // Collapse the panel and clear the form
        this.tts.visible = false
        this.tts.text = ''
      } catch (e) {
        this.tts.error = e.message || 'Generation failed — check API key configuration'
      } finally {
        this.tts.generating = false
      }
    },

    onUploadFileSelect (e) {
      this.upload.file = e.target.files[0] || null
      this.upload.error = ''
    },

    onUploadDrop (e) {
      this.upload.dragging = false
      const file = e.dataTransfer.files[0]
      if (!file) return
      if (!/\.(mp3|wav)$/i.test(file.name) && !/^audio\/(mpeg|wav)$/.test(file.type)) {
        this.upload.error = 'Only MP3 and WAV files are supported'
        return
      }
      this.upload.file = file
      this.upload.error = ''
      if (!this.upload.name) this.upload.name = file.name.replace(/\.[^.]+$/, '')
    },

    async uploadAudioInline () {
      if (!this.upload.file) return
      this.upload.uploading = true
      this.upload.error = ''
      try {
        const fd = new FormData()
        fd.append('audio', this.upload.file)
        if (this.upload.name.trim()) fd.append('name', this.upload.name.trim())
        const res = await IVRService.uploadAudio(fd)
        // Refresh the library list
        const listRes = await IVRService.getAudio()
        this.audioFiles = listRes.files || []
        // Auto-select the uploaded file on the node
        const fileId = res.id
        this.selectedNodeData.config.audio_file_id = fileId
        const file = this.audioFiles.find(f => f.id === fileId)
        this.selectedNodeData.config.audio_file_name = file ? file.name : (this.upload.name.trim() || this.upload.file.name)
        this.onNodeConfigChange()
        // Collapse and reset
        this.upload.visible = false
        this.upload.file = null
        this.upload.name = ''
      } catch (e) {
        this.upload.error = e.message || 'Upload failed'
      } finally {
        this.upload.uploading = false
      }
    },

    // ── API Call node helpers ──────────────────────────────────────────────
    ensureApiCallConfig (node) {
      const c = node.config
      if (!c.headers) c.headers = {}
      if (!c.response_mapping) c.response_mapping = {}
      if (c.request_body_json === undefined) c.request_body_json = ''
      if (!c.api_url) c.api_url = ''
      if (!c.http_method) c.http_method = 'POST'
      if (!c.query_params) c.query_params = {}
      if (c.api_node_name === undefined) c.api_node_name = ''
      if (!c.body_format) c.body_format = 'json'
      if (!c.form_params) c.form_params = {}
      if (!c.idmsg_mode) c.idmsg_mode = 'off'
      if (c.idmsg_custom_value === undefined) c.idmsg_custom_value = ''
      if (c.skip_ssl_verify === undefined) c.skip_ssl_verify = false
      return c
    },

    // ── Variable helpers ───────────────────────────────────────────────────

    /** BFS backwards from nodeId — returns Set of all ancestor node IDs. */
    getUpstreamNodeIds (nodeId) {
      const visited = new Set()
      const queue = [nodeId]
      while (queue.length) {
        const cur = queue.shift()
        if (visited.has(cur)) continue
        visited.add(cur)
        for (const n of this.flowNodes) {
          if (visited.has(n.id)) continue
          if (n.next === cur) { queue.push(n.id); continue }
          if (n.routes && Object.values(n.routes).includes(cur)) queue.push(n.id)
        }
      }
      visited.delete(nodeId)
      return visited
    },

    /** Track the last focused plain input / textarea inside the config panel. */
    onIvrInputFocus (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        this.focusedIvrInput = e.target
      }
    },

    /**
     * Insert {{varName}} at the cursor position.
     * Works with both the VarTextEditor component (api_call) and plain inputs.
     */
    insertVariableAtCursor (varName) {
      if (this.activeVarEditor) {
        this.activeVarEditor.insertVariable(varName)
        return
      }
      const el = this.focusedIvrInput
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return
      const start = el.selectionStart != null ? el.selectionStart : el.value.length
      const end = el.selectionEnd != null ? el.selectionEnd : el.value.length
      const insertion = `{{${varName}}}`
      el.value = el.value.slice(0, start) + insertion + el.value.slice(end)
      // Trigger Vue's v-model to pick up the new value
      el.dispatchEvent(new Event('input', { bubbles: true }))
      const pos = start + insertion.length
      el.setSelectionRange(pos, pos)
      el.focus()
    },

    addHeader (node) {
      const c = this.ensureApiCallConfig(node)
      let key = 'Authorization'
      if (key in c.headers) key = `Header_${Date.now()}`
      node.config.headers = { ...c.headers, [key]: '' }
      this.onNodeConfigChange()
    },
    removeHeader (node, key) {
      delete node.config.headers[key]
      this.onNodeConfigChange()
    },
    updateHeaderKey (node, oldKey, newKey) {
      if (oldKey === newKey || !newKey) return
      const val = node.config.headers[oldKey]
      delete node.config.headers[oldKey]
      node.config.headers[newKey] = val
      this.onNodeConfigChange()
    },

    addQueryParam (node) {
      const c = this.ensureApiCallConfig(node)
      node.config.query_params = { ...c.query_params, param_name: '' }
      this.onNodeConfigChange()
    },
    removeQueryParam (node, key) {
      delete node.config.query_params[key]
      this.onNodeConfigChange()
    },
    updateQueryParam (node, oldKey, newKey, value) {
      if (oldKey !== newKey && newKey) {
        delete node.config.query_params[oldKey]
        node.config.query_params[newKey] = value
      } else {
        node.config.query_params[oldKey] = value
      }
      this.onNodeConfigChange()
    },

    addFormParam (node) {
      const c = this.ensureApiCallConfig(node)
      node.config.form_params = { ...c.form_params, field_name: '' }
      this.onNodeConfigChange()
    },
    removeFormParam (node, key) {
      delete node.config.form_params[key]
      this.onNodeConfigChange()
    },
    updateFormParam (node, oldKey, newKey, value) {
      if (oldKey !== newKey && newKey) {
        delete node.config.form_params[oldKey]
        node.config.form_params[newKey] = value
      } else {
        node.config.form_params[oldKey] = value
      }
      this.onNodeConfigChange()
    },

    addResponseMapping (node) {
      const c = this.ensureApiCallConfig(node)
      node.config.response_mapping = { ...c.response_mapping, 'response.key': 'variable_name' }
      this.onNodeConfigChange()
    },
    removeResponseMapping (node, key) {
      delete node.config.response_mapping[key]
      this.onNodeConfigChange()
    },
    updateResponseMapping (node, oldKey, newKey, value) {
      if (oldKey === newKey || !newKey) return
      delete node.config.response_mapping[oldKey]
      node.config.response_mapping[newKey] = value
      this.onNodeConfigChange()
    },

    prepareApiTest (node) {
      const c = node.config
      const allText = [
        c.api_url || '',
        c.request_body_json || '',
        ...Object.values(c.headers || {}),
        ...Object.values(c.query_params || {})
      ].join(' ')
      const found = new Set()
      let m
      const pattern = /\{\{([^}]+)\}\}/g
      while ((m = pattern.exec(allText)) !== null) found.add(m[1].trim())
      const varList = Array.from(found)
      if (varList.length === 0) {
        this.apiTestVarsDetected = []
        this.runApiTest(node)
        return
      }
      varList.forEach(v => {
        if (this.testSampleValues[v] === undefined || this.testSampleValues[v] === '') {
          this.testSampleValues[v] = ''
        }
      })
      this.apiTestVarsDetected = varList
    },

    async runApiTest (node) {
      this.testingApi = true
      this.apiTestResponse = null
      this.apiTestError = null
      this.apiTestNodeId = node.id
      try {
        const c = node.config
        const samples = this.testSampleValues
        const replaceVars = text => typeof text === 'string'
          ? text.replace(/\{\{([^}]+)\}\}/g, (_, k) => samples[k.trim()] || `[${k.trim()}]`)
          : text

        let url = replaceVars(c.api_url || '')
        if (!url) throw new Error('API URL is required')

        if (c.http_method === 'GET' && c.query_params) {
          const params = new URLSearchParams()
          Object.keys(c.query_params).forEach(k => { const v = replaceVars(c.query_params[k]); if (v) params.append(k, v) })
          const qs = params.toString()
          if (qs) url = url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`
        }

        const headers = { 'Content-Type': 'application/json' }
        Object.keys(c.headers || {}).forEach(k => { headers[k] = replaceVars(c.headers[k]).replace(/\u00a0/g, ' ') })

        const result = await DashboardService.proxyApiTest({
          url,
          method: c.http_method || 'POST',
          headers,
          body: ['POST', 'PUT', 'PATCH'].includes(c.http_method) ? replaceVars(c.request_body_json || '{}') : null,
          timeout: c.timeout_seconds || 30,
          skip_ssl_verify: !!c.skip_ssl_verify,
          body_format: c.body_format || 'json',
          form_params: c.form_params || {}
        })

        if (result.error && !result.status_code) throw new Error(result.error)
        this.apiTestResponse = result.response
      } catch (e) {
        this.apiTestError = e.message
      } finally {
        this.testingApi = false
      }
    },

    // ─── Ported helpers ─────────────────────────────────────────────────────────

    isGoogleNodeType (type) {
      return [
        'google_sheets_read', 'google_sheets_append', 'google_sheets_update',
        'google_drive_upload', 'google_drive_create_folder',
        'google_docs_create', 'google_docs_append'
      ].includes(type)
    },

    isMicrosoftNodeType (type) {
      return [
        'excel_read', 'excel_append', 'excel_update',
        'onedrive_upload', 'onedrive_create_folder'
      ].includes(type)
    },

    // ─── Tags & Templates ───────────────────────────────────────────────────────

    async loadAvailableTags () {
      try {
        const res = await DashboardService.getCustomerTags()
        this.availableTags = res.tags || res || []
      } catch (e) { /* silent */ }
    },

    async loadApprovedTemplates () {
      try {
        const res = await DashboardService.getApprovedTemplates()
        this.approvedTemplates = res.templates || res || []
      } catch (e) { /* silent */ }
    },

    // ─── Google connections ──────────────────────────────────────────────────────

    async loadGoogleConnections () {
      try {
        const res = await DashboardService.getGoogleConnections()
        this.googleConnections = res.connections || res || []
      } catch (e) { /* silent */ }
    },

    async loadGsSpreadsheets (connectionId) {
      if (!connectionId) return
      this.gsLoadingSpreadsheets = true
      try {
        const res = await DashboardService.getGoogleSpreadsheets(connectionId)
        this.gsSpreadsheets = res.spreadsheets || []
      } finally { this.gsLoadingSpreadsheets = false }
    },

    async loadGsSheetsInSpread (connectionId, spreadsheetId) {
      if (!connectionId || !spreadsheetId) return
      this.gsLoadingSheets = true
      try {
        const res = await DashboardService.getGoogleSheets(connectionId, spreadsheetId)
        this.gsSheetsInSpread = res.sheets || []
      } finally { this.gsLoadingSheets = false }
    },

    async loadGsColumnHeaders (connectionId, spreadsheetId, sheetName) {
      if (!connectionId || !spreadsheetId || !sheetName) return
      this.gsLoadingHeaders = true
      try {
        const res = await DashboardService.getGoogleSheetHeaders(connectionId, spreadsheetId, sheetName)
        this.gsColumnHeaders = res.headers || []
        const nd = this.selectedNodeData
        if (nd && nd.ivrType === 'google_sheets_read' && this.gsColumnHeaders.length && !nd.config.sheet_columns?.length) {
          nd.config.sheet_columns = [...this.gsColumnHeaders]
          this.onNodeConfigChange()
        }
      } finally { this.gsLoadingHeaders = false }
    },

    async onGsConnectionChange () {
      this.gsSpreadsheets = []
      this.gsSheetsInSpread = []
      this.gsColumnHeaders = []
      const nd = this.selectedNodeData
      if (nd) { nd.config.spreadsheet_id = ''; nd.config.sheet_name = '' }
      this.onNodeConfigChange()
      const cid = nd?.config?.google_connection_id
      if (cid) await this.loadGsSpreadsheets(cid)
    },

    async onGsSpreadsheetChange () {
      this.gsSheetsInSpread = []
      this.gsColumnHeaders = []
      const nd = this.selectedNodeData
      if (nd) nd.config.sheet_name = ''
      this.onNodeConfigChange()
      const c = nd?.config || {}
      if (c.google_connection_id && c.spreadsheet_id) await this.loadGsSheetsInSpread(c.google_connection_id, c.spreadsheet_id)
    },

    async onGsSheetChange () {
      this.gsColumnHeaders = []
      this.onNodeConfigChange()
      const c = this.selectedNodeData?.config || {}
      if (c.google_connection_id && c.spreadsheet_id && c.sheet_name) await this.loadGsColumnHeaders(c.google_connection_id, c.spreadsheet_id, c.sheet_name)
    },

    async loadGdFolders (connectionId) {
      if (!connectionId) return
      this.gdLoadingFolders = true
      try {
        const res = await DashboardService.getGoogleDriveFolders(connectionId)
        this.gdFolders = res.folders || []
      } finally { this.gdLoadingFolders = false }
    },

    async onDriveConnectionChange () {
      this.gdFolders = []
      this.onNodeConfigChange()
      const cid = this.selectedNodeData?.config?.google_connection_id
      if (cid) await this.loadGdFolders(cid)
    },

    // ─── Microsoft connections ────────────────────────────────────────────────────

    async loadMicrosoftConnections () {
      try {
        const res = await DashboardService.getMicrosoftConnections()
        this.microsoftConnections = res.connections || res || []
      } catch (e) { /* silent */ }
    },

    async loadMsWorkbooks (connectionId) {
      if (!connectionId) return
      this.msLoadingWorkbooks = true
      try {
        const res = await DashboardService.getMicrosoftWorkbooks(connectionId)
        this.msWorkbooks = res.workbooks || []
      } finally { this.msLoadingWorkbooks = false }
    },

    async loadMsWorksheetsInBook (connectionId, workbookId) {
      if (!connectionId || !workbookId) return
      this.msLoadingWorksheets = true
      try {
        const res = await DashboardService.getMicrosoftWorksheets(connectionId, workbookId)
        this.msWorksheetsInBook = res.sheets || []
      } finally { this.msLoadingWorksheets = false }
    },

    async loadMsColumnHeaders (connectionId, workbookId, sheetName) {
      if (!connectionId || !workbookId || !sheetName) return
      this.msLoadingHeaders = true
      try {
        const res = await DashboardService.getMicrosoftWorksheetHeaders(connectionId, workbookId, sheetName)
        this.msColumnHeaders = res.headers || []
        const nd = this.selectedNodeData
        if (nd && nd.ivrType === 'excel_read' && this.msColumnHeaders.length) {
          nd.config.sheet_columns = [...this.msColumnHeaders]
          this.onNodeConfigChange()
        }
      } finally { this.msLoadingHeaders = false }
    },

    async onMsConnectionChange () {
      this.msWorkbooks = []
      this.msWorksheetsInBook = []
      this.msColumnHeaders = []
      const nd = this.selectedNodeData
      if (nd) { nd.config.workbook_id = ''; nd.config.worksheet_name = '' }
      this.onNodeConfigChange()
      const cid = nd?.config?.microsoft_connection_id
      if (cid) await this.loadMsWorkbooks(cid)
    },

    async onMsWorkbookChange () {
      this.msWorksheetsInBook = []
      this.msColumnHeaders = []
      const nd = this.selectedNodeData
      if (nd) nd.config.worksheet_name = ''
      this.onNodeConfigChange()
      const c = nd?.config || {}
      if (c.microsoft_connection_id && c.workbook_id) await this.loadMsWorksheetsInBook(c.microsoft_connection_id, c.workbook_id)
    },

    async onMsWorksheetChange () {
      this.msColumnHeaders = []
      this.onNodeConfigChange()
      const c = this.selectedNodeData?.config || {}
      if (c.microsoft_connection_id && c.workbook_id && c.worksheet_name) await this.loadMsColumnHeaders(c.microsoft_connection_id, c.workbook_id, c.worksheet_name)
    },

    // ─── Row builder helpers ──────────────────────────────────────────────────────

    _serializeRowItems (items) {
      const obj = {}
      items.forEach(item => { if (item.col) obj[item.col] = item.val })
      return JSON.stringify(obj)
    },

    onRowDataItemChange () {
      if (this.selectedNodeData) {
        this.selectedNodeData.config.row_data_json = this._serializeRowItems(this.rowDataItems)
        this.onNodeConfigChange()
      }
    },

    onSetDataItemChange () {
      if (this.selectedNodeData) {
        this.selectedNodeData.config.set_data_json = this._serializeRowItems(this.setDataItems)
        this.onNodeConfigChange()
      }
    },

    /** Returns gsColumnHeaders minus columns already chosen in other rows. */
    gsAvailableCols (items, currentIndex) {
      const used = new Set(
        items.filter((_, i) => i !== currentIndex).map(it => it.col).filter(Boolean)
      )
      return this.gsColumnHeaders.filter(h => !used.has(h))
    },

    /** Same as gsAvailableCols but for Microsoft column headers. */
    msAvailableCols (items, currentIndex) {
      const used = new Set(
        items.filter((_, i) => i !== currentIndex).map(it => it.col).filter(Boolean)
      )
      return this.msColumnHeaders.filter(h => !used.has(h))
    },

    syncMsRowData (mode) {
      const items = mode === 'append' ? this.msRowDataItems : this.msSetDataItems
      const obj = {}
      items.forEach(item => { if (item.col) obj[item.col] = item.val })
      const json = JSON.stringify(obj)
      if (this.selectedNodeData) {
        if (mode === 'append') this.selectedNodeData.config.row_data_json = json
        else this.selectedNodeData.config.set_data_json = json
        this.onNodeConfigChange()
      }
    },

    // ─── Template selector ────────────────────────────────────────────────────────

    onSendTemplateSelected (node) {
      const tpl = this.approvedTemplates.find(t => t.name === node.config.template_name)
      if (!tpl) {
        node.config.template_language = ''
        node.config.template_components = []
        node.config.header_type = ''
        node.config.header_params = []
        node.config.header_media_url = ''
        node.config.body_params = []
        node.config.button_params = []
        this.onNodeConfigChange()
        return
      }
      node.config.template_language = tpl.language
      node.config.template_components = tpl.components || []
      const headerComp = (tpl.components || []).find(c => c.type === 'HEADER')
      if (headerComp) {
        const fmt = headerComp.format || 'TEXT'
        node.config.header_type = fmt
        if (fmt === 'TEXT') {
          const count = (headerComp.text || '').match(/\{\{\d+\}\}/g)?.length || 0
          node.config.header_params = Array.from({ length: count }, () => ({ value: '' }))
        } else {
          node.config.header_params = []
          node.config.header_media_url = ''
        }
      } else {
        node.config.header_type = ''
        node.config.header_params = []
        node.config.header_media_url = ''
      }
      const bodyComp = (tpl.components || []).find(c => c.type === 'BODY')
      const bodyVarCount = bodyComp ? ((bodyComp.text || '').match(/\{\{\d+\}\}/g)?.length || 0) : 0
      node.config.body_params = Array.from({ length: bodyVarCount }, () => ({ value: '' }))
      const buttonsComp = (tpl.components || []).find(c => c.type === 'BUTTONS')
      const btnParams = []
      if (buttonsComp?.buttons) {
        buttonsComp.buttons.forEach((btn, idx) => {
          if (btn.type === 'URL' && (btn.url || '').match(/\{\{\d+\}\}/)) {
            btnParams.push({ button_index: idx, button_text: btn.text, value: '' })
          }
        })
      }
      node.config.button_params = btnParams
      this.onNodeConfigChange()
    },

    // ─── Condition nodes ──────────────────────────────────────────────────────────

    addConditionForNode (node) {
      if (!node.config.conditions) node.config.conditions = []
      const idx = node.config.conditions.length + 1
      node.config.conditions.push({ id: `cond_${Date.now()}`, variable: '', operator: '==', value: '' })
      // add a matching route
      const routeKey = `if_${idx}`
      if (node.routes === undefined) node.routes = {}
      if (!(routeKey in node.routes)) node.routes[routeKey] = ''
      this.onNodeConfigChange()
    },

    removeConditionForNode (node, index) {
      if (!node.config.conditions) return
      node.config.conditions.splice(index, 1)
      // rebuild routes to match conditions
      const newRoutes = {}
      node.config.conditions.forEach((_, i) => { newRoutes[`if_${i + 1}`] = node.routes?.[`if_${i + 1}`] || '' })
      newRoutes.else = node.routes?.else || ''
      node.routes = newRoutes
      this.onNodeConfigChange()
    },

    addTagConditionForNode (node) {
      if (!Array.isArray(node.config.conditions)) node.config.conditions = []
      const idx = node.config.conditions.length + 1
      node.config.conditions.push({ id: `tagcond_${Date.now()}`, tag_id: '' })
      if (node.routes === undefined) node.routes = {}
      const routeKey = `if_${idx}`
      if (!(routeKey in node.routes)) node.routes[routeKey] = ''
      this.onNodeConfigChange()
    },

    removeTagConditionForNode (node, index) {
      if (!Array.isArray(node.config.conditions)) return
      node.config.conditions.splice(index, 1)
      const newRoutes = {}
      node.config.conditions.forEach((_, i) => { newRoutes[`if_${i + 1}`] = node.routes?.[`if_${i + 1}`] || '' })
      newRoutes.else = node.routes?.else || ''
      node.routes = newRoutes
      this.onNodeConfigChange()
    },

    // ─── Calculator ────────────────────────────────────────────────────────────────

    addCalcRow (node) {
      if (!node.config.calculations) node.config.calculations = []
      node.config.calculations.push({ operand_a: '', operator: '+', operand_b: '', result_variable: '' })
      this.onNodeConfigChange()
    },

    removeCalcRow (node, idx) {
      node.config.calculations.splice(idx, 1)
      this.onNodeConfigChange()
    },

    calcOpNeedsB (op) {
      return !['floor', 'ceil', 'abs'].includes(op)
    },

    // ─── Date/Time helpers ────────────────────────────────────────────────────────

    offsetDayLabel (n) {
      n = parseInt(n) || 0
      if (n === 0) return 'today'
      if (n === 1) return 'tomorrow'
      if (n === 2) return 'day after tomorrow'
      if (n === -1) return 'yesterday'
      if (n === -2) return 'day before yesterday'
      return n > 0 ? `${n} days from now` : `${Math.abs(n)} days ago`
    },

    datetimeSampleValues (node) {
      const base = new Date()
      const offsetDays = parseInt(node.config?.offset_days || 0) || 0
      const skipWeekends = !!node.config?.skip_weekends
      const now = new Date(base)
      now.setDate(now.getDate() + offsetDays)
      const weekendDays = Array.isArray(node.config?.weekend_days) ? node.config.weekend_days.map(Number) : []
      if (skipWeekends && weekendDays.length > 0) {
        let safety = 7
        while (weekendDays.includes(now.getDay()) && safety-- > 0) now.setDate(now.getDate() + 1)
      }
      const pad = n => String(n).padStart(2, '0')
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const d = pad(now.getDate())
      const j = String(now.getDate())
      const m = pad(now.getMonth() + 1)
      const Y = String(now.getFullYear())
      const M = monthsShort[now.getMonth()]
      const F = months[now.getMonth()]
      const H = pad(now.getHours())
      const G = String(now.getHours())
      const i = pad(now.getMinutes())
      const s = pad(now.getSeconds())
      const h12 = now.getHours() % 12 || 12
      const h = pad(h12)
      const A = now.getHours() < 12 ? 'AM' : 'PM'
      const N = now.getDay() === 0 ? 7 : now.getDay()
      const phpFormat = fmt => fmt
        .replace(/d/g, d).replace(/j/g, j).replace(/m/g, m).replace(/Y/g, Y)
        .replace(/M/g, M).replace(/F/g, F)
        .replace(/H/g, H).replace(/G/g, G).replace(/i/g, i).replace(/s/g, s)
        .replace(/h/g, h).replace(/A/g, A)
      const dateFmt = node.config?.date_format || 'd/m/Y'
      const timeFmt = node.config?.time_format || 'H:i'
      const p = node.config?.variable_prefix || 'now'
      return [
        { key: `${p}_date`, val: phpFormat(dateFmt) },
        { key: `${p}_time`, val: phpFormat(timeFmt) },
        { key: `${p}_datetime`, val: phpFormat(dateFmt) + ' ' + phpFormat(timeFmt) },
        { key: `${p}_day`, val: days[now.getDay()] },
        { key: `${p}_day_number`, val: String(N) },
        { key: `${p}_hour`, val: G },
        { key: `${p}_minute`, val: String(now.getMinutes()) }
      ]
    },

    onSkipWeekendsToggle (node) {
      if (!Array.isArray(node.config.weekend_days)) node.config.weekend_days = [0, 6]
    },

    toggleWeekendDay (node, dayValue, event) {
      if (!Array.isArray(node.config.weekend_days)) node.config.weekend_days = []
      const days = node.config.weekend_days.map(Number)
      if (event.target.checked) {
        if (!days.includes(dayValue)) days.push(dayValue)
      } else {
        const idx = days.indexOf(dayValue)
        if (idx !== -1) days.splice(idx, 1)
      }
      node.config.weekend_days = days
      this.onNodeConfigChange()
    },

    sanitizeColName (col) {
      return col.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    },

    getNodeDescription (type) {
      const d = {
        ivr_play: 'Plays a pre-recorded audio file to the caller.',
        ivr_menu: 'Plays an audio prompt then waits for the caller to press a digit key (DTMF).',
        ivr_collect_input: 'Plays a prompt and collects a digit sequence (e.g. phone number) storing it in a named variable accessible downstream via {{variable_name}}.',
        ivr_get_speech: 'Plays a prompt, records the caller\'s spoken response, and transcribes it to text.',
        ivr_record_voicemail: 'Plays a prompt then records a voicemail message from the caller.',
        ivr_wait: 'Holds the call in silence for a set number of seconds.',
        ivr_transfer: 'Transfers the call directly to an Asterisk extension or agent.',
        ivr_queue: 'Places the caller into a call queue — all assigned agents will ring.',
        ivr_end: 'Hangs up the call and ends the IVR session.',
        ivr_condition: 'Evaluates a condition on a flow variable and branches to true or false.',
        ivr_set_variable: 'Sets a named flow variable referenceable by later nodes as {{variable}}.',
        ivr_goto: 'Jumps unconditionally to another node — useful for loops and shared paths.',
        ivr_business_hours: 'Branches the flow based on whether current time is within business hours.',
        ivr_blacklist_check: 'Checks whether the caller\'s number appears on the IVR blacklist.',
        ivr_lookup_customer: 'Searches the CRM for a customer matching the caller\'s phone number.',
        ivr_assign_tag: 'Attaches a tag to the matched customer\'s CRM record.',
        ivr_create_note: 'Adds a note to the customer\'s CRM record.',
        ivr_send_whatsapp: 'Sends a WhatsApp template message to the caller\'s phone number.',
        ivr_webhook: 'Makes an HTTP request to an external URL and can store the response.',
        api_call: 'Makes a full REST API call with custom headers, query params, JSON/form body, and response variable mapping.',
        ivr_ai_response: 'Generates an AI text response via GPT using the caller\'s context variables.'
      }
      return d[type] || ''
    }
  }
}
</script>

<style scoped>
/* ══════════════════════════════════════════════════
   IVR FLOW BUILDER — n8n-style dark theme
   ══════════════════════════════════════════════════ */
.ivr-builder-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  font-family: inherit;
  --bg-primary: #1e1f2e;
  --bg-secondary: #252637;
  --border-color: rgba(255,255,255,0.08);
  --text-primary: #e2e8f0;
  --text-muted: #6b7080;
  background: #13131f;
}

/* ── Top Bar ─────────────────────────────────────────────────────────── */
.ivr-builder-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 18px;
  height: 56px;
  background: #1a1a2c;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
  z-index: 10;
  box-shadow: 0 1px 0 rgba(255,255,255,0.04);
}

.ivr-builder-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
  transition: all 0.15s;
}
.ivr-builder-back:hover { background: rgba(255,255,255,0.06); color: #e2e8f0; border-color: rgba(255,255,255,0.18); }

.ivr-builder-flow-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.ivr-builder-name-input {
  font-size: 15px;
  font-weight: 700;
  border: none;
  background: transparent;
  color: #e2e8f0;
  padding: 5px 8px;
  border-radius: 6px;
  min-width: 200px;
  transition: background 0.15s;
}
.ivr-builder-name-input:focus { outline: 2px solid #6366F1; outline-offset: -1px; background: rgba(255,255,255,0.05); }

.ivr-builder-did-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 5px 11px;
  color: #4b5563;
  background: rgba(255,255,255,0.04);
}
.ivr-builder-did-input {
  border: none;
  background: transparent;
  font-size: 13px;
  color: #cbd5e1;
  min-width: 160px;
}
.ivr-builder-did-input:focus { outline: none; }
.ivr-builder-did-input::placeholder { color: #4b5563; }

.ivr-builder-topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.ivr-save-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #4b5563;
  min-width: 72px;
  font-weight: 500;
}
.ivr-save-indicator.saving { color: #818cf8; }
.ivr-save-indicator.saved { color: #22C55E; }
.ivr-save-indicator.error { color: #EF4444; }
.ivr-save-indicator.unsaved { color: #F59E0B; }

.ivr-manual-save-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 7px;
  border: 1px solid rgba(245,158,11,0.4);
  background: rgba(245,158,11,0.12);
  color: #F59E0B;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.ivr-manual-save-btn:hover { background: rgba(245,158,11,0.22); }

.ivr-autosave-toggle {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,0.1);
  background: transparent;
  color: #6b7080;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.ivr-autosave-toggle:hover { background: rgba(255,255,255,0.05); color: #a0a8b8; }
.ivr-autosave-toggle.on { color: #818cf8; }
.ivr-autosave-track {
  width: 28px;
  height: 16px;
  border-radius: 8px;
  background: rgba(255,255,255,0.12);
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
}
.ivr-autosave-toggle.on .ivr-autosave-track { background: #6366f1; }
.ivr-autosave-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}
.ivr-autosave-toggle.on .ivr-autosave-thumb { transform: translateX(12px); }

.ivr-builder-status-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 16px;
  border-radius: 20px;
  border: 1px solid;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.18s;
}
.ivr-builder-status-btn .status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ivr-builder-status-btn.active { background: rgba(34,197,94,0.1); color: #22C55E; border-color: rgba(34,197,94,0.3); }
.ivr-builder-status-btn.active .status-dot { background: #22C55E; box-shadow: 0 0 0 2px rgba(34,197,94,0.25); }
.ivr-builder-status-btn.inactive { background: rgba(99,102,241,0.1); color: #818cf8; border-color: rgba(99,102,241,0.3); }
.ivr-builder-status-btn.inactive .status-dot { background: #818cf8; }
.ivr-builder-status-btn.inactive:hover { background: rgba(99,102,241,0.18); }

/* ── Workspace ───────────────────────────────────────────────────────── */
.ivr-builder-workspace {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.ivr-builder-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #374151;
  font-size: 14px;
}

/* ── Palette ─────────────────────────────────────────────────────────── */
.ivr-palette {
  width: 256px;
  flex-shrink: 0;
  background: #1a1a2c;
  border-right: 1px solid rgba(255,255,255,0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.2s;
}
.ivr-palette.collapsed { width: 40px; }

.ivr-palette-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #4b5563;
  cursor: pointer;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
  user-select: none;
}
.ivr-palette-header:hover { background: rgba(255,255,255,0.03); }
.palette-title { display: flex; align-items: center; gap: 7px; }
.palette-chevron { flex-shrink: 0; }

.ivr-palette-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  background: rgba(0,0,0,0.18);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.palette-search-icon { color: rgba(255,255,255,0.3); flex-shrink: 0; }
.palette-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #e2e8f0;
  font-size: 12px;
  min-width: 0;
}
.palette-search-input::placeholder { color: rgba(255,255,255,0.25); }
.palette-search-clear {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.palette-search-clear:hover { color: rgba(255,255,255,0.7); }
.palette-no-results {
  padding: 20px 12px;
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.3);
}

.ivr-palette-body {
  overflow-y: auto;
  flex: 1;
  padding: 10px 8px;
}
.ivr-palette-body::-webkit-scrollbar { width: 4px; }
.ivr-palette-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

.ivr-palette-group { margin-bottom: 16px; }
.ivr-palette-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #374151;
  padding: 2px 8px 6px;
}

.ivr-palette-node {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 9px;
  cursor: grab;
  border-left: 3px solid var(--node-color, #94a3b8);
  font-size: 13px;
  color: #94a3b8;
  transition: background 0.12s;
  margin-bottom: 2px;
  user-select: none;
}
.ivr-palette-node:hover {
  background: color-mix(in srgb, var(--node-color, #94a3b8) 14%, transparent);
  color: #e2e8f0;
}
.ivr-palette-node:active { cursor: grabbing; }

.ivr-palette-node-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--node-color, #94a3b8) 18%, transparent);
  color: var(--node-color, #94a3b8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ivr-palette-node-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.ivr-palette-node-name { font-size: 12px; font-weight: 600; color: #cbd5e1; }
.ivr-palette-node-desc { font-size: 10px; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Canvas ──────────────────────────────────────────────────────────── */
.ivr-canvas {
  flex: 1;
  position: relative;
  overflow: hidden;
  cursor: default;
  background-color: #13131f;
  background-image: radial-gradient(rgba(255,255,255,0.09) 1.5px, transparent 1.5px);
  background-size: 24px 24px;
}

.ivr-edge-layer {
  position: absolute;
  top: 0;
  left: 0;
}

.ivr-nodes-layer {
  position: absolute;
  top: 0;
  left: 0;
}

.ivr-edge-group { cursor: pointer; }
.ivr-edge { pointer-events: none; }
.ivr-edge-group:hover .ivr-edge { stroke-opacity: 1; }
.ivr-edge.selected { stroke: #F97316; stroke-opacity: 1; }

.ivr-edge-disconnect-btn {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #F97316;
  color: #fff;
  border: 2px solid #13131f;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 200;
  box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  transition: transform 0.1s, background 0.1s;
  pointer-events: all;
}
.ivr-edge-disconnect-btn:hover {
  background: #ef4444;
  transform: translate(-50%, -50%) scale(1.15);
}

/* ── Node Cards — n8n dark style ─────────────────────────────────────── */
.ivr-node {
  position: absolute;
  width: 210px;
  background: #1f2030;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, transform 0.1s;
}
.ivr-node:hover {
  border-color: rgba(255,255,255,0.22);
  transform: translateY(-1px);
}
.ivr-node.selected {
  border-color: rgba(99,102,241,0.65);
  outline: 2px solid rgba(99,102,241,0.25);
}
.ivr-node.is-start::before {
  content: '';
  position: absolute;
  top: -5px; left: -5px; right: -5px; bottom: -5px;
  border: 1.5px dashed rgba(99,102,241,0.5);
  border-radius: 14px;
  pointer-events: none;
}

/* n8n-style: colored icon column on left, text on right */
.ivr-node-inner {
  display: flex;
  align-items: stretch;
  min-height: 62px;
  border-radius: 9px;
  overflow: hidden;
}

.ivr-node-icon-col {
  width: 44px;
  background: var(--node-accent, #94a3b8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgba(255,255,255,0.92);
}

.ivr-node-text-col {
  flex: 1;
  padding: 10px 8px 10px 11px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.ivr-node-name {
  font-size: 12px;
  font-weight: 700;
  color: #e2e8f0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ivr-node-sub {
  font-size: 10px;
  color: rgba(255,255,255,0.35);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ivr-node-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  padding: 0 6px 0 2px;
  border: none;
  background: transparent;
  color: rgba(255,255,255,0.2);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
  flex-shrink: 0;
}
.ivr-node:hover .ivr-node-delete-btn { opacity: 1; }
.ivr-node-delete-btn:hover { color: #f87171; }

.ivr-node-start-badge {
  position: absolute;
  top: -18px;
  left: 44px;
  font-size: 9px;
  font-weight: 800;
  background: #6366F1;
  color: #fff;
  padding: 1px 7px;
  border-radius: 4px 4px 0 0;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

/* ── Handles ─────────────────────────────────────────────────────────── */
.ivr-handle {
  position: absolute;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  cursor: crosshair;
  transition: transform 0.12s, box-shadow 0.12s;
  z-index: 2;
}
.ivr-handle:hover { transform: scale(1.45) !important; }

.ivr-handle-input {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  background: #6366F1;
  border: 2px solid #1f2030;
  box-shadow: 0 0 0 1.5px #6366F1;
}
.ivr-handle-input:hover { transform: translateX(-50%) scale(1.4) !important; }

.ivr-handle-output {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2030;
  border: 2.5px solid #6366F1;
  box-shadow: 0 0 0 1px rgba(99,102,241,0.4);
}
.ivr-handle-output:hover { transform: translateX(-50%) scale(1.4) !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.25); }

.ivr-handle-route {
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translateX(-50%);
  border-radius: 3px;
  background: var(--node-accent, #94a3b8);
  border: 2px solid #1f2030;
  box-shadow: 0 0 0 1px var(--node-accent, #94a3b8);
}
.ivr-handle-route:hover { transform: translateX(-50%) scale(1.4) !important; }

.ivr-route-label {
  position: absolute;
  left: 18px;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  background: rgba(20,20,40,0.9);
  color: var(--node-accent, #94a3b8);
  padding: 1px 6px;
  border-radius: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid color-mix(in srgb, var(--node-accent, #94a3b8) 35%, transparent);
  pointer-events: none;
}

/* ── Canvas hint ─────────────────────────────────────────────────────── */
.ivr-canvas-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #374151;
  font-size: 14px;
  text-align: center;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
}

/* ── Config Panel ────────────────────────────────────────────────────── */
.ivr-config-panel {
  width: 320px;
  flex-shrink: 0;
  background: #1a1a2c;
  border-left: 1px solid rgba(255,255,255,0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ivr-config-header {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  background: color-mix(in srgb, var(--node-accent, #6366F1) 8%, #1a1a2c);
}

.ivr-config-header-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.ivr-config-icon-badge {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: color-mix(in srgb, var(--node-accent, #6366F1) 20%, transparent);
  color: var(--node-accent, #6366F1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ivr-config-type {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--node-accent, #6366F1);
  margin-bottom: 3px;
}

.ivr-config-name-input {
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: #e2e8f0;
  padding: 0;
  width: 100%;
  line-height: 1.3;
}
.ivr-config-name-input:focus { outline: 2px solid #6366F1; border-radius: 4px; padding: 2px 4px; margin: -2px -4px; }

.ivr-config-header-actions { display: flex; gap: 6px; flex-shrink: 0; }

.ivr-config-set-start, .ivr-config-delete {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.05);
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: #6b7080;
  transition: all 0.15s;
  white-space: nowrap;
}
.ivr-config-set-start:hover, .ivr-config-set-start.active { background: rgba(99,102,241,0.15); color: #818cf8; border-color: rgba(99,102,241,0.35); }
.ivr-config-delete:hover { background: rgba(239,68,68,0.12); color: #f87171; border-color: rgba(239,68,68,0.3); }

.ivr-config-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
}
.ivr-config-body::-webkit-scrollbar { width: 4px; }
.ivr-config-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

.ivr-field-group { margin-bottom: 14px; }
.ivr-field-group label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #94a3b8;
}
.ivr-var-hint {
  font-size: 10px;
  font-weight: 400;
  color: #4b5563;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  padding: 1px 6px;
  border-radius: 10px;
}
.ivr-field-group-next { margin-top: 18px; padding-top: 14px; border-top: 1px solid rgba(255,255,255,0.07); }
.ivr-transfer-var-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.ivr-transfer-var-row .ivr-var-hint {
  white-space: nowrap;
  flex-shrink: 0;
}
.ivr-input-sm {
  padding: 5px 10px;
  font-size: 12px;
  flex: 1;
}
.ivr-field-row { display: flex; gap: 8px; }
.ivr-field-row .ivr-field-group { flex: 1; }

.ivr-input, .ivr-select, .ivr-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  font-size: 13px;
  background: #252637;
  color: #e2e8f0;
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ivr-input:focus, .ivr-select:focus, .ivr-textarea:focus {
  outline: none;
  border-color: #6366F1;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
}
.ivr-textarea { resize: vertical; min-height: 64px; line-height: 1.5; }
.ivr-select-sm { font-size: 12px; padding: 5px 8px; }
/* Solid background on options so text is legible in every browser */
.ivr-select option { background: #252637; color: #e2e8f0; }

.ivr-config-info {
  font-size: 12px;
  color: #4b5563;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 10px 12px;
  line-height: 1.6;
  margin: 0;
}

.ivr-section-hint {
  font-size: 9px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #374151;
  margin-left: 6px;
}

.ivr-routes-table { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
.ivr-route-row { display: flex; align-items: center; gap: 8px; }

.ivr-route-key-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 110px;
}
.ivr-route-key {
  font-size: 12px;
  font-weight: 700;
  color: #818cf8;
  font-family: ui-monospace, monospace;
  background: rgba(99,102,241,0.12);
  padding: 3px 8px;
  border-radius: 6px;
  text-align: center;
  flex-shrink: 0;
  min-width: 28px;
}
.ivr-route-key-desc {
  font-size: 11px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ivr-route-wire-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #9ca3af;
  flex: 1;
  white-space: nowrap;
}
.ivr-route-remove-btn {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #374151;
  border-radius: 4px;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
}
.ivr-route-remove-btn:hover { background: rgba(239,68,68,0.12); color: #f87171; }

.ivr-routes-empty {
  font-size: 12px;
  color: #374151;
  text-align: center;
  padding: 10px;
  font-style: italic;
}

.ivr-add-key-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  padding: 6px 12px;
  background: transparent;
  color: #818cf8;
  border: 1px dashed rgba(99,102,241,0.4);
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: all 0.15s;
}
.ivr-add-key-btn:hover:not(:disabled) { background: rgba(99,102,241,0.08); border-color: #818cf8; }
.ivr-add-key-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.ivr-route-row-pending { opacity: 0.85; }

/* ── API Call node ───────────────────────────────────────────────────── */
.ivr-kv-pair {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
}
.ivr-kv-key {
  flex: 0 0 110px;
  min-width: 0;
}
.ivr-kv-vte {
  flex: 1;
  min-width: 0;
}
.ivr-kv-val {
  flex: 1;
  min-width: 0;
}
.ivr-kv-remove {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ivr-kv-remove:hover { background: rgba(239,68,68,0.12); color: #ef4444; }
.ivr-add-row-btn {
  font-size: 11px;
  color: #818cf8;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 0;
}
.ivr-add-row-btn:hover { text-decoration: underline; }
.ivr-field-hint {
  font-size: 10px;
  color: #6b7280;
  margin-top: 3px;
  display: block;
}
.ivr-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
  cursor: pointer;
}
.ivr-api-var-picker {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 10px;
}
.ivr-api-var-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 11px;
  color: #6b7280;
}
.ivr-api-var-search {
  flex: 1;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  color: #e2e8f0;
  outline: none;
}
.ivr-api-var-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.ivr-var-chip-pick {
  font-size: 10px;
  background: rgba(99,102,241,0.15);
  color: #818cf8;
  border-radius: 20px;
  padding: 2px 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;
}
.ivr-var-chip-pick:hover { background: rgba(99,102,241,0.3); }
.ivr-api-test-samples {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}
.ivr-api-test-samples-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 11px;
  color: #9ca3af;
}
.ivr-api-test-sample-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}
.ivr-api-test-tag {
  font-size: 10px;
  background: rgba(6,182,212,0.15);
  color: #22d3ee;
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
  flex-shrink: 0;
}
.ivr-api-response-pre {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 6px;
  padding: 8px;
  font-size: 10px;
  color: #86efac;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ── Spinner ─────────────────────────────────────────────────────────── */
.ivr-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #6366F1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin 1s linear infinite; }

/* ── Config panel enrichments ────────────────────────────────────────── */
.ivr-node-description {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.55;
  margin: 0 0 14px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
  border-left: 3px solid color-mix(in srgb, var(--node-accent, #6366F1) 55%, transparent);
}

.ivr-section-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #374151;
  margin: 14px 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.ivr-section-label:first-of-type { margin-top: 0; }
.ivr-section-label-output {
  color: #818cf8;
  border-bottom-color: rgba(99,102,241,0.2);
}

/* ── Audio picker ─── */
.ivr-audio-selected {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 10px;
  background: color-mix(in srgb, var(--node-accent, #6366F1) 8%, rgba(255,255,255,0.04));
  border: 1px solid color-mix(in srgb, var(--node-accent, #6366F1) 28%, transparent);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #cbd5e1;
}
.ivr-audio-selected svg { color: var(--node-accent, #6366F1); flex-shrink: 0; }
.ivr-audio-selected span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ivr-audio-preview-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 9px;
  background: var(--node-accent, #6366F1);
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;
}
.ivr-audio-preview-btn:hover { opacity: 0.85; }
.ivr-audio-preview-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ivr-btn-spinner {
  width: 9px;
  height: 9px;
  border: 1.5px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: ivr-spin 0.7s linear infinite;
}
@keyframes ivr-spin { to { transform: rotate(360deg); } }
/* ── Single player card ── */
.ivr-audio-mini-player {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 5px 7px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px;
}
/* Loading row (inside .ivr-audio-selected) */
.ivr-mini-spinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid rgba(255,255,255,0.25);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ivr-spin 0.7s linear infinite;
  flex-shrink: 0;
  vertical-align: middle;
}
/* Play/Pause button */
.ivr-mini-playpause {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  border: none;
  background: var(--node-accent, #6366f1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: opacity 0.15s;
}
.ivr-mini-playpause:hover { opacity: 0.8; }
/* Scrub bar */
.ivr-mini-progress {
  flex: 1;
  height: 4px;
  background: rgba(255,255,255,0.12);
  border-radius: 2px;
  cursor: pointer;
  overflow: hidden;
}
.ivr-mini-fill {
  height: 100%;
  background: var(--node-accent, #6366f1);
  border-radius: 2px;
  transition: width 0.2s linear;
  max-width: 100%;
}
/* Time */
.ivr-mini-time {
  font-size: 10px;
  color: rgba(255,255,255,0.5);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 60px;
  text-align: center;
}
/* Stop button */
.ivr-mini-stop {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 5px;
  border: 1px solid rgba(255,255,255,0.15);
  background: transparent;
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s;
}
.ivr-mini-stop:hover { border-color: #fca5a5; color: #f87171; background: rgba(239,68,68,0.12); }

.ivr-link-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 11px;
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
}
.ivr-link-hint:hover { text-decoration: underline; }

/* ── Variable chips ─── */
.ivr-vars-set {
  margin-top: 14px;
  padding: 10px 12px;
  background: rgba(34,197,94,0.07);
  border: 1px solid rgba(34,197,94,0.2);
  border-radius: 8px;
}
.ivr-vars-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #22C55E;
  margin-bottom: 6px;
}
.ivr-var-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.ivr-var-chip {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(34,197,94,0.12);
  color: #4ade80;
  border: 1px solid rgba(34,197,94,0.25);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  font-family: ui-monospace, monospace;
}

/* ── Output routes ─── */
.ivr-output-routes { display: flex; flex-direction: column; gap: 6px; }
.ivr-output-route {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid;
}
.ivr-output-true  { background: rgba(34,197,94,0.07); border-color: rgba(34,197,94,0.2); }
.ivr-output-false { background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.2); }
.ivr-output-route-key {
  font-size: 11px;
  font-weight: 700;
  font-family: ui-monospace, monospace;
  min-width: 68px;
}
.ivr-output-true  .ivr-output-route-key { color: #4ade80; }
.ivr-output-false .ivr-output-route-key { color: #f87171; }
.ivr-output-route-desc {
  font-size: 11px;
  color: #4b5563;
}

/* ── Output next section ─── */
.ivr-output-next-section { margin-top: 4px; }

/* ── End node icon ─── */
.ivr-end-icon-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 0;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
}

/* ── Delete confirmation modal ───────────────────────────────────────── */
.ivr-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(3px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ivr-modal {
  background: #1e1f2e;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 28px 28px 24px;
  width: 340px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}

.ivr-modal-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(239,68,68,0.12);
  color: #f87171;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.ivr-modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #e2e8f0;
}

.ivr-modal-body {
  font-size: 13px;
  color: #6b7080;
  line-height: 1.6;
}

.ivr-modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  width: 100%;
}

.ivr-modal-btn {
  flex: 1;
  padding: 10px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid;
  transition: all 0.15s;
}

.ivr-modal-btn-cancel {
  background: rgba(255,255,255,0.05);
  color: #94a3b8;
  border-color: rgba(255,255,255,0.1);
}
.ivr-modal-btn-cancel:hover { background: rgba(255,255,255,0.09); }

.ivr-modal-btn-danger {
  background: #e11d48;
  color: #fff;
  border-color: #e11d48;
}
.ivr-modal-btn-danger:hover { background: #be123c; border-color: #be123c; }

.ivr-modal-fade-enter-active, .ivr-modal-fade-leave-active { transition: opacity 0.18s; }
.ivr-modal-fade-enter, .ivr-modal-fade-leave-to { opacity: 0; }

/* ── Inline TTS Generator ────────────────────────────────────────────── */
.ivr-tts-panel {
  margin-top: 10px;
}

.ivr-tts-toggle {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 8px 12px;
  background: rgba(99,102,241,0.08);
  border: 1px solid rgba(99,102,241,0.2);
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #818cf8;
  transition: background 0.15s, border-color 0.15s;
  text-align: left;
}
.ivr-tts-toggle:hover { background: rgba(99,102,241,0.14); border-color: rgba(99,102,241,0.35); }

.ivr-tts-chevron {
  margin-left: auto;
  transition: transform 0.18s;
}
.ivr-tts-chevron.open { transform: rotate(180deg); }

.ivr-tts-form {
  margin-top: 10px;
  padding: 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Provider tabs */
.ivr-tts-tabs {
  display: flex;
  gap: 4px;
  background: rgba(0,0,0,0.2);
  border-radius: 8px;
  padding: 3px;
}

.ivr-tts-tab {
  flex: 1;
  padding: 5px 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.ivr-tts-tab.active {
  background: #6366F1;
  color: #fff;
  box-shadow: 0 1px 4px rgba(99,102,241,0.4);
}
.ivr-tts-tab:not(.active):hover { color: #e2e8f0; }

/* Override field-group margin inside the form */
.ivr-tts-form .ivr-field-group { margin-bottom: 0; }
.ivr-tts-form .ivr-field-group label { color: #6b7080; }

.ivr-tts-error {
  font-size: 11px;
  color: #f87171;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.2);
  border-radius: 6px;
  padding: 6px 10px;
  margin: 0;
  line-height: 1.5;
}

.ivr-tts-generate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  padding: 9px 14px;
  background: linear-gradient(135deg, #6366F1, #8B5CF6);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  letter-spacing: 0.01em;
}
.ivr-tts-generate-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.ivr-tts-generate-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

.ivr-drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 18px 12px;
  border: 1.5px dashed rgba(99,102,241,0.35);
  border-radius: 10px;
  background: rgba(99,102,241,0.04);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  text-align: center;
  min-height: 80px;
}
.ivr-drop-zone:hover,
.ivr-drop-zone.drag-over {
  border-color: #818cf8;
  background: rgba(99,102,241,0.10);
}
.ivr-drop-zone.drag-over { border-style: solid; }
.ivr-drop-zone.has-file {
  border-color: rgba(34,197,94,0.4);
  background: rgba(34,197,94,0.04);
  flex-direction: row;
  gap: 8px;
  padding: 12px;
  min-height: unset;
}
.ivr-drop-zone.has-file:hover { border-color: rgba(34,197,94,0.7); background: rgba(34,197,94,0.08); }
.ivr-drop-icon { color: #818cf8; opacity: 0.7; }
.ivr-drop-text { font-size: 12px; font-weight: 600; color: #a0a8c0; }
.ivr-drop-hint { font-size: 10px; color: #6b7080; }
.ivr-drop-file-name { font-size: 11px; font-weight: 600; color: #22c55e; flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Ported node CSS ── */

/* Condition / Check Tag rule rows */
.condition-rule-row {
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  background: rgba(255,255,255,0.02);
}
.condition-rule-row.condition-else-row { background: rgba(99,102,241,0.04); border-color: rgba(99,102,241,0.15); }
.condition-rule-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.condition-rule-badge { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 8px; border-radius: 10px; background: rgba(99,102,241,0.15); color: #818cf8; }
.else-badge { background: rgba(148,163,184,0.12); color: #94a3b8; }
.btn-remove-condition { background: none; border: none; cursor: pointer; color: #f87171; font-size: 16px; line-height: 1; padding: 0 2px; }
.condition-rule-fields { display: flex; flex-direction: column; gap: 6px; margin-bottom: 6px; }
.condition-port-hint { font-size: 10px; color: #64748b; display: flex; align-items: center; gap: 4px; padding-top: 4px; }

/* Calculator */
.calc-cards { display: flex; flex-direction: column; gap: 8px; }
.calc-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 10px; }
.calc-eq-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; }
.calc-operand { flex: 1; min-width: 80px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #e2e8f0; font-size: 12px; padding: 6px 8px; }
.calc-operand--active { border-color: #6366F1; background: rgba(99,102,241,0.08); }
.calc-op-select { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #e2e8f0; font-size: 12px; padding: 6px 8px; }
.calc-result-row { display: flex; align-items: center; gap: 6px; }
.calc-save-label { font-size: 11px; color: #64748b; white-space: nowrap; }
.calc-result-input { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #e2e8f0; font-size: 12px; padding: 6px 8px; }
.calc-remove-btn { background: none; border: none; cursor: pointer; color: #f87171; font-size: 14px; padding: 0 4px; }
.calc-empty { text-align: center; font-size: 12px; color: #64748b; padding: 12px; }

/* Delay input */
.delay-input-row { display: flex; gap: 8px; align-items: center; }
.delay-value-input { width: 80px; }
.delay-unit-select { flex: 1; }

/* Weekend day picker */
.weekend-days-picker { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.weekend-day-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.03); cursor: pointer; font-size: 11px; color: #94a3b8; user-select: none; }
.weekend-day-chip input[type=checkbox] { display: none; }
.weekend-day-chip.active { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.4); color: #818cf8; font-weight: 600; }

/* Checkbox label */
.ivr-checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: #cbd5e1; }
.ivr-checkbox-label input[type=checkbox] { width: 14px; height: 14px; cursor: pointer; }

/* QR size pills */
.ivr-size-pill { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #94a3b8; font-size: 12px; font-weight: 600; padding: 5px 12px; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 2px; transition: all 0.15s; }
.ivr-size-pill:hover { background: rgba(255,255,255,0.08); }
.ivr-size-pill--active { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.4); color: #818cf8; }

/* Add button */
.ivr-btn-add { background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2); border-radius: 6px; color: #818cf8; font-size: 12px; font-weight: 600; padding: 6px 14px; cursor: pointer; transition: all 0.15s; }
.ivr-btn-add:hover { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.4); }

/* Google/Microsoft picker rows */
.gs-picker-row { display: flex; gap: 6px; align-items: center; }
.btn-gs-refresh { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #94a3b8; font-size: 13px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: all 0.15s; }
.btn-gs-refresh:hover:not(:disabled) { background: rgba(255,255,255,0.1); color: #e2e8f0; }
.btn-gs-refresh:disabled { opacity: 0.35; cursor: not-allowed; }

/* Filter row */
.filter-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.filter-row .ivr-input, .filter-row .ivr-select { flex: 1; min-width: 80px; }

/* Row builder */
.row-builder { display: flex; flex-direction: column; gap: 6px; margin-bottom: 6px; }
.row-builder-item { display: flex; gap: 6px; align-items: center; }
.row-builder-col { flex: 1; }
.row-builder-val { flex: 2; }
.btn-row-remove { background: none; border: none; cursor: pointer; color: #f87171; font-size: 16px; padding: 0 3px; flex-shrink: 0; }
.btn-row-add { background: none; border: 1px dashed rgba(255,255,255,0.15); border-radius: 6px; color: #64748b; font-size: 12px; padding: 5px 10px; cursor: pointer; width: 100%; transition: all 0.15s; }
.btn-row-add:hover { border-color: rgba(99,102,241,0.3); color: #818cf8; }

/* ── Variable Bar ─────────────────────────────────────────────────── */
.ivr-vars-bar {
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.07);
  background: rgba(0,0,0,0.2);
  margin-bottom: 12px;
  overflow: hidden;
}
.ivr-vars-bar-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  cursor: pointer;
  font-size: 11px;
  color: #94a3b8;
  user-select: none;
  transition: background 0.12s;
}
.ivr-vars-bar-toggle:hover { background: rgba(255,255,255,0.04); }
.ivr-vars-bar-toggle span { flex: none; }
.ivr-vars-bar-count {
  margin-left: 2px;
  background: rgba(99,102,241,0.2);
  color: #818cf8;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 6px;
  flex: none;
}
.ivr-vars-bar-chevron { margin-left: auto; transition: transform 0.18s; flex-shrink: 0; }
.ivr-vars-bar-chevron.open { transform: rotate(180deg); }
.ivr-vars-chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 8px 10px 10px;
  border-top: 1px solid rgba(255,255,255,0.05);
  align-items: flex-start;
}
.ivr-var-chip {
  display: inline-block;
  font-size: 10px;
  font-family: monospace;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid;
  cursor: pointer;
  user-select: none;
  line-height: 1.6;
  transition: transform 0.1s, opacity 0.1s;
}
.ivr-var-chip:hover { transform: translateY(-1px); opacity: 0.85; }
.ivr-var-chip:active { transform: translateY(0); }
/* source colours */
.chip-src-system  { background: #1e3a5f; border-color: #3b82f6; color: #93c5fd; }
.chip-src-input   { background: #0c2a35; border-color: #06b6d4; color: #67e8f9; }
.chip-src-set     { background: #2e1065; border-color: #a855f7; color: #d8b4fe; }
.chip-src-crm     { background: #052e16; border-color: #22c55e; color: #86efac; }
.chip-src-ai      { background: #4a044e; border-color: #d946ef; color: #f0abfc; }
.chip-src-api     { background: #431407; border-color: #f97316; color: #fdba74; }
.chip-src-calc    { background: #1a2e05; border-color: #84cc16; color: #bef264; }
.chip-src-datetime{ background: #082f49; border-color: #0ea5e9; color: #7dd3fc; }
.chip-src-data    { background: #0d2a1f; border-color: #10b981; color: #6ee7b7; }
.ivr-vars-empty { font-size: 11px; color: #4b5563; font-style: italic; }
.ivr-vars-insert-hint {
  width: 100%;
  font-size: 10px;
  color: #374151;
  margin: 4px 0 0;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.04);
}
.ivr-vars-insert-hint code {
  background: rgba(255,255,255,0.07);
  border-radius: 3px;
  padding: 1px 4px;
  font-family: monospace;
  font-size: 10px;
  color: #6b7280;
}
</style>
